import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../lib/api";
import { Plus, Trash2, Clock, Euro, CalendarClock, Settings, X, Link2, Check, Clock4, Users, BarChart3, ShieldCheck, Code2, Mail, CreditCard, Bell, Sparkles, Heart, Hourglass, Package as PackageIcon, BookUser, SlidersHorizontal } from "lucide-react";
import EmailSettings from "../../components/EmailSettings";
import StripeSettings from "../../components/StripeSettings";
import PushToggle from "../../components/PushToggle";
import ReminderCadenceCard from "../../components/ReminderCadenceCard";
import SuggestionsPanel from "../../components/SuggestionsPanel";
import RegularsPanel from "../../components/RegularsPanel";
import WaitlistPanel from "../../components/WaitlistPanel";
import PackagesPanel from "../../components/PackagesPanel";
import OnboardingWizard from "../../components/OnboardingWizard";
import BetaWelcomeModal from "../../components/BetaWelcomeModal";
import BusinessPrivacyEditor from "../../components/BusinessPrivacyEditor";
import ChangePasswordCard from "../../components/ChangePasswordCard";
import ContactsPanel from "../../components/ContactsPanel";
import CustomerPicker from "../../components/CustomerPicker";
import StaffAccessPanel from "../../components/StaffAccessPanel";
import { useAuth } from "../../context/AuthContext";
import { useCapabilities } from "../../context/CapabilitiesContext";
// Field helper is shared with extracted tabs (services/staff/clients/…)
import _Field from "./components/Field";
// Tab body components extracted from this shell (P2 refactor).
import Services from "./tabs/Services";
import Staff from "./tabs/Staff";
import Clients from "./tabs/Clients";
import Blocks from "./tabs/Blocks";
import WorkingHours from "./tabs/WorkingHours";
import HolidaysSection from "./tabs/HolidaysSection";
import { formatLocalDate } from "./utils/formatLocalDate";

// UI tab → capability mapping. Stay in sync with the backend matrix in
// `routes/staff_access.py`. To gate a new tab, list its required capability
// here and the tab will appear/disappear automatically based on the payload
// returned by GET /api/staff/capabilities.
const TAB_CAPABILITY = {
  calendar: "appointments:view",
  suggestions: "suggestions:view",
  regulars: "regulars:view",
  waitlist: "waitlist:manage",
  analytics: "analytics:view",
  services: "services:view",
  packages: "packages:manage",
  staff: "staff:view",
  clients: "customers:view",
  contacts: "contacts:manage",
  hours: "hours:manage",
  blocks: "blocks:manage",
  rules: "rules:manage",
  privacy: "legal:view",
  widget: "widget:view",
  email: "email:manage",
  stripe: "stripe:manage",
  notifications: "notifications:manage",
};

const CATS = [
  { key: "palestra", label: "Palestra" },
  { key: "estetica", label: "Estetica" },
  { key: "parrucchiere", label: "Parrucchiere" },
  { key: "studio", label: "Studio Professionale" },
];

export default function BusinessDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { can, role: capabilityRole, refresh: refreshCaps } = useCapabilities();
  const [biz, setBiz] = useState(null);
  const [tab, setTab] = useState("calendar");
  const [loading, setLoading] = useState(true);

  // accessRole comes from the unified capability payload so we never re-derive
  // role membership client-side. Fall back to owner during the first paint to
  // avoid hiding tabs from a legitimate owner while the call is in flight.
  const accessRole = capabilityRole || "owner";
  const isOwner = accessRole === "owner";

  useEffect(() => {
    api.get("/my/business").then((r) => {
      setBiz(r.data);
      setLoading(false);
      // Ensure the capability payload is scoped to *this* business — owners
      // don't carry business_id on their user record, so the initial fetch in
      // CapabilitiesContext returns role=null. Re-fetch with the resolved id.
      if (r.data?.id) refreshCaps(r.data.id);
    });
  }, [refreshCaps]);

  if (loading) return <div className="p-10 text-sm text-zinc-500 dark:text-zinc-400">Caricamento…</div>;
  if (!biz) {
    if (user?.role === "staff_user") {
      return <div className="max-w-md mx-auto p-10 text-center"><div className="font-display text-xl">Accesso non disponibile</div><p className="text-sm text-zinc-500 dark:text-zinc-400 mt-2">L'attività a cui sei stato invitato non è più accessibile. Contatta il titolare.</p></div>;
    }
    return <Setup onCreated={(b) => { setBiz(b); nav("/business"); }} />;
  }

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">{biz.category}</div>
          <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">{biz.name}</h1>
          <div className="text-sm text-zinc-500 dark:text-zinc-400">{biz.city}{biz.address ? ` · ${biz.address}` : ""}</div>
        </div>
      </div>

      {isOwner && (
        <>
          <ShareLinkBanner businessId={biz.id} />
          <DpaBanner />
          <OnboardingWizard business={biz} onJumpTab={(t) => { setTab(t); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
          <QuickBlockBanner businessId={biz.id} />
        </>
      )}
      {!isOwner && (
        <div className="mb-6 rounded-2xl bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 px-4 py-3 text-xs text-blue-900 dark:text-blue-200 flex items-center gap-2" data-testid="staff-access-banner">
          <span className="inline-flex h-6 w-6 rounded-full bg-blue-600 text-white items-center justify-center text-[10px] font-semibold">{accessRole === "manager" ? "R" : "S"}</span>
          <span>Accesso staff — ruolo <strong>{accessRole === "manager" ? "Responsabile" : "Staff"}</strong>. Lo staff può accedere solo alle aree consentite.</span>
        </div>
      )}

      <div className="flex gap-2 overflow-x-auto hide-scrollbar mb-6">
        {[
          { k: "calendar", label: "Agenda", icon: CalendarClock },
          { k: "suggestions", label: "Consigli", icon: Sparkles },
          { k: "regulars", label: "Abituali", icon: Heart },
          { k: "waitlist", label: "Attesa", icon: Hourglass },
          { k: "analytics", label: "Analytics", icon: BarChart3 },
          { k: "services", label: "Servizi", icon: Settings },
          { k: "packages", label: "Pacchetti", icon: PackageIcon },
          { k: "staff", label: "Staff", icon: Users },
          { k: "clients", label: "Clienti", icon: Users },
          { k: "contacts", label: "Rubrica", icon: BookUser },
          { k: "hours", label: "Orari", icon: Clock4 },
          { k: "blocks", label: "Ferie", icon: X },
          { k: "rules", label: "Regole", icon: ShieldCheck },
          { k: "privacy", label: "Privacy", icon: ShieldCheck },
          { k: "widget", label: "Widget", icon: Code2 },
          { k: "email", label: "Email", icon: Mail },
          { k: "stripe", label: "Pagamenti", icon: CreditCard },
          { k: "notifications", label: "Notifiche", icon: Bell },
        ].filter((t) => !TAB_CAPABILITY[t.k] || can(TAB_CAPABILITY[t.k])).map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`h-10 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap ${tab === t.k ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
            data-testid={`biz-tab-${t.k}`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "calendar" && <Agenda businessId={biz.id} />}
      {tab === "suggestions" && <SuggestionsPanel businessId={biz.id} />}
      {tab === "regulars" && <RegularsPanel businessId={biz.id} />}
      {tab === "waitlist" && <WaitlistPanel businessId={biz.id} />}
      {tab === "analytics" && <Analytics businessId={biz.id} />}
      {tab === "services" && <Services businessId={biz.id} />}
      {tab === "packages" && <PackagesPanel businessId={biz.id} />}
      {tab === "staff" && <Staff businessId={biz.id} isOwner={isOwner} />}
      {tab === "clients" && <Clients businessId={biz.id} />}
      {tab === "contacts" && <ContactsPanel businessId={biz.id} />}
      {tab === "hours" && <WorkingHours businessId={biz.id} />}
      {tab === "blocks" && <Blocks businessId={biz.id} />}
      {tab === "rules" && <Rules businessId={biz.id} />}
      {tab === "privacy" && <BusinessPrivacyEditor businessId={biz.id} />}
      {tab === "widget" && <WidgetEmbed businessId={biz.id} />}
      {tab === "email" && <EmailSettings businessId={biz.id} />}
      {tab === "stripe" && <StripeSettings businessId={biz.id} />}
      {tab === "notifications" && (
        <div className="max-w-xl space-y-6" data-testid="biz-notifications-panel">
          <div>
            <h2 className="font-display text-xl font-medium mb-1">Notifiche push</h2>
            <p className="text-sm text-zinc-600 mb-4 dark:text-zinc-400">
              Ricevi un avviso istantaneo su questo dispositivo quando un cliente prenota, cancella o modifica un appuntamento. Attivale su ciascun dispositivo da cui usi Prenotoo.
            </p>
            <PushToggle />
          </div>
          <ReminderCadenceCard businessId={biz.id} />
        </div>
      )}

      <GdprAccountSection />
      <BetaWelcomeModal />
    </div>
  );
}


function GdprAccountSection() {
  const nav = useNavigate();
  async function exportData() {
    const { data } = await api.get("/me/export");
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `prenotoo-account-export-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  }

  async function deleteAccount() {
    try {
      const { data, status } = await api.post("/me/delete-request");
      if (status === 200 && data?.sent_to) {
        alert(`Ti abbiamo inviato un link di conferma a ${data.sent_to}. Cliccalo entro ${data.expires_in_min} minuti per completare la cancellazione.`);
        return;
      }
    } catch (ex) {
      if (ex.response?.status !== 202) {
        alert(ex.response?.data?.detail || "Errore nell'invio email. Riprova.");
        return;
      }
    }
    const ok = window.confirm(
      "Cancellare definitivamente l'account e l'attività?\n\n" +
      "L'attività verrà rimossa (servizi, staff, orari, ferie).\n" +
      "Le prenotazioni dei clienti verranno anonimizzate per obblighi fiscali.\n" +
      "L'azione è irreversibile.\n\n" +
      "Procedere?"
    );
    if (!ok) return;
    try {
      await api.delete("/me");
      localStorage.removeItem("token");
      nav("/");
      setTimeout(() => window.location.reload(), 100);
    } catch (e) {
      alert("Errore nella cancellazione. Contatta il supporto.");
    }
  }

  return (
    <div className="mt-12 pt-8 border-t border-zinc-100 dark:border-zinc-800" data-testid="biz-gdpr-section">
      <h2 className="font-display text-xl font-medium">Sicurezza</h2>
      <p className="text-sm text-zinc-500 mt-1 mb-4 dark:text-zinc-400">Mantieni il tuo account business protetto.</p>
      <ChangePasswordCard />

      <h2 className="font-display text-xl font-medium mt-10">Privacy e dati account</h2>
      <p className="text-sm text-zinc-500 mt-1 mb-4 dark:text-zinc-400">Esercita i tuoi diritti GDPR (art. 15, 17, 20).</p>
      <div className="flex flex-wrap gap-3">
        <button onClick={exportData} className="btn-secondary h-11 text-sm" data-testid="biz-gdpr-export-btn">
          Esporta i miei dati (JSON)
        </button>
        <button onClick={deleteAccount} className="h-11 px-5 rounded-full text-sm font-medium border border-red-300 text-red-700 hover:bg-red-50 dark:text-red-300 dark:bg-red-950/40" data-testid="biz-gdpr-delete-btn">
          Cancella account e attività
        </button>
      </div>
    </div>
  );
}

function ShareLinkBanner({ businessId }) {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/book/${businessId}`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  }

  return (
    <div className="relative overflow-hidden rounded-3xl bg-zinc-900 text-white p-5 md:p-6 mb-6 grain-bg" data-testid="share-link-banner">
      <div className="relative flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-blue-300 mb-1">
            Il tuo link di prenotazione
          </div>
          <div className="flex items-center gap-2 text-white/90">
            <Link2 className="h-4 w-4 shrink-0" />
            <a href={link} target="_blank" rel="noreferrer" className="text-sm md:text-base truncate hover:underline" data-testid="share-link-url">
              {link}
            </a>
          </div>
          <div className="text-xs text-zinc-400 mt-1 dark:text-zinc-500">Condividilo con i tuoi clienti su Instagram, WhatsApp o sul tuo sito.</div>
        </div>
        <div className="flex gap-2">
          <a href={link} target="_blank" rel="noreferrer" className="btn-secondary h-11 text-sm text-zinc-900 dark:text-zinc-100" data-testid="share-link-preview">
            Anteprima
          </a>
          <button onClick={copy} className="btn-accent h-11 text-sm" data-testid="share-link-copy">
            {copied ? <><Check className="h-4 w-4 mr-2" />Copiato</> : "Copia link"}
          </button>
        </div>
      </div>
    </div>
  );
}


function QuickBlockBanner({ businessId }) {
  const [active, setActive] = useState(null);
  const [opening, setOpening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    api.get(`/businesses/${businessId}/active-block`).then((r) => setActive(r.data)).catch(() => setActive(null));
  }, [businessId]);

  useEffect(() => { load(); }, [load]);

  async function unblock() {
    if (!active || busy) return;
    setBusy(true);
    setErr("");
    try {
      await api.delete(`/businesses/${businessId}/blocks/${active.id}`);
      setActive(null);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore durante lo sblocco. Riprova.");
      // Also try to reload state to stay in sync with backend
      load();
    } finally {
      setBusy(false);
    }
  }

  if (active) {
    const end = new Date(active.end_at);
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 mb-4 relative z-30 dark:border-rose-800/50 dark:bg-rose-950/40" data-testid="quickblock-active">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm">
            <div className="font-semibold text-rose-900 dark:text-rose-200">🚫 Prenotazioni bloccate</div>
            <div className="text-rose-800 mt-0.5 dark:text-rose-300">
              {active.reason || "Prenotazioni sospese"} · fino a <strong>{end.toLocaleString("it-IT", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</strong>
            </div>
          </div>
          <button
            onClick={unblock}
            disabled={busy}
            className="h-10 px-4 rounded-full bg-white border border-rose-300 text-rose-700 hover:bg-rose-100 text-sm font-medium whitespace-nowrap disabled:opacity-50 dark:bg-zinc-900 dark:text-rose-300"
            data-testid="quickblock-unblock"
          >
            {busy ? "Sblocco…" : "Sblocca ora"}
          </button>
        </div>
        {err && (
          <div className="mt-2 text-xs text-rose-900 bg-rose-100 border border-rose-200 rounded-lg px-3 py-2 dark:text-rose-200 dark:bg-rose-950/40 dark:border-rose-800/50" data-testid="quickblock-unblock-error">
            ⚠ {err}
          </div>
        )}
      </div>
    );
  }
  return (
    <>
      <div className="flex justify-end mb-3" data-testid="quickblock-trigger-row">
        <button onClick={() => setOpening(true)} className="inline-flex items-center gap-1.5 h-9 px-3 text-xs rounded-full border border-zinc-200 bg-white hover:border-rose-300 hover:text-rose-700 text-zinc-600 font-medium transition dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400" data-testid="quickblock-open">
          <span>🚫</span> Blocca prenotazioni
        </button>
      </div>
      {opening && <QuickBlockModal businessId={businessId} onClose={() => setOpening(false)} onSaved={() => { setOpening(false); load(); }} />}
    </>
  );
}


function QuickBlockModal({ businessId, onClose, onSaved }) {
  const [preset, setPreset] = useState("end-of-day");
  const [customDate, setCustomDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(23, 59, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  function untilFromPreset() {
    const d = new Date();
    if (preset === "end-of-day") { d.setHours(23, 59, 0, 0); return d; }
    if (preset === "tomorrow-end") { d.setDate(d.getDate() + 1); d.setHours(23, 59, 0, 0); return d; }
    if (preset === "week-end") {
      // Next Sunday 23:59
      const day = d.getDay(); // 0=Sun
      const toSun = day === 0 ? 0 : 7 - day;
      d.setDate(d.getDate() + toSun);
      d.setHours(23, 59, 0, 0);
      return d;
    }
    if (preset === "custom") return new Date(customDate);
    return d;
  }

  async function submit() {
    setErr("");
    const until = untilFromPreset();
    if (!(until instanceof Date) || isNaN(until.getTime()) || until <= new Date()) {
      setErr("Data non valida");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/businesses/${businessId}/quick-block`, {
        until: toLocalIso(until),
        reason: reason.trim() || "Prenotazioni sospese",
      });
      onSaved();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Errore");
    } finally {
      setSaving(false);
    }
  }

  const previewDate = untilFromPreset();

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="quickblock-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.15em] uppercase text-rose-600">Blocca prenotazioni</div>
        <h2 className="font-display text-2xl font-medium mt-1">Fino a quando?</h2>
        <p className="text-sm text-zinc-600 mt-1 dark:text-zinc-400">I clienti non potranno prenotare online in questo periodo. Gli appuntamenti già confermati restano validi.</p>

        <div className="mt-5 space-y-2">
          {[
            { k: "end-of-day", label: "Resto di oggi" },
            { k: "tomorrow-end", label: "Oggi + domani" },
            { k: "week-end", label: "Fino a fine settimana" },
            { k: "custom", label: "Data personalizzata" },
          ].map((p) => (
            <label key={p.k} className={`flex items-center gap-3 rounded-xl border p-3 cursor-pointer transition ${preset === p.k ? "border-zinc-900 bg-zinc-50" : "border-zinc-200 hover:border-zinc-300"}`} data-testid={`quickblock-preset-${p.k}`}>
              <input type="radio" name="preset" checked={preset === p.k} onChange={() => setPreset(p.k)} className="h-4 w-4" />
              <span className="text-sm font-medium">{p.label}</span>
            </label>
          ))}
        </div>

        {preset === "custom" && (
          <div className="mt-3">
            <input type="datetime-local" className="input-soft" value={customDate} onChange={(e) => setCustomDate(e.target.value)} data-testid="quickblock-custom-date" />
          </div>
        )}

        <div className="mt-4">
          <label className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Motivo (facoltativo)</label>
          <input type="text" className="input-soft mt-1" maxLength={120} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. Formazione, pulizie straordinarie…" data-testid="quickblock-reason" />
        </div>

        {previewDate instanceof Date && !isNaN(previewDate.getTime()) && (
          <div className="mt-4 text-xs text-zinc-500 dark:text-zinc-400">
            Blocco attivo fino a <strong>{previewDate.toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}</strong>.
          </div>
        )}
        {err && <div className="mt-3 text-sm text-red-600" data-testid="quickblock-error">{err}</div>}

        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1" disabled={saving}>Annulla</button>
          <button onClick={submit} className="btn-accent flex-1 bg-rose-600 hover:bg-rose-700" disabled={saving} data-testid="quickblock-confirm">
            {saving ? "Blocco…" : "🚫 Blocca"}
          </button>
        </div>
      </div>
    </div>
  );
}


function DpaBanner() {
  const [status, setStatus] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    api.get("/my/dpa-status").then((r) => setStatus(r.data)).catch(() => setStatus({ accepted: true }));
  }, []);

  async function accept() {
    setAccepting(true);
    try {
      await api.post("/my/accept-dpa");
      setStatus({ accepted: true, accepted_at: new Date().toISOString() });
    } finally {
      setAccepting(false);
    }
  }

  if (!status || status.accepted) return null;

  return (
    <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 md:p-6 mb-6 dark:border-amber-800/50 dark:bg-amber-950/40" data-testid="dpa-banner">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="text-[11px] font-semibold tracking-[0.2em] uppercase text-amber-700 mb-1 dark:text-amber-300">
            Accordo richiesto
          </div>
          <div className="text-sm font-medium text-amber-900 dark:text-amber-200">
            Accetta il DPA (Data Processing Agreement) per continuare a utilizzare Prenotoo
          </div>
          <div className="text-xs text-amber-800 mt-1 dark:text-amber-300">
            Documento richiesto dal GDPR per regolare il trattamento dei dati dei tuoi clienti. <a href="/dpa" target="_blank" rel="noreferrer" className="underline">Leggi il DPA</a>
          </div>
        </div>
        <button onClick={accept} disabled={accepting} className="btn-accent h-11 text-sm shrink-0" data-testid="dpa-accept-btn">
          {accepting ? "Salvataggio…" : "Accetto il DPA"}
        </button>
      </div>
    </div>
  );
}



function Setup({ onCreated }) {
  const [form, setForm] = useState({ name: "", category: "estetica", city: "", address: "", description: "", cover_image: "", phone: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.post("/businesses", form);
      onCreated(data);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 md:px-8 py-10">
      <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight">Crea la tua attività</h1>
      <p className="text-zinc-500 mt-2 dark:text-zinc-400">Pochi dati per iniziare a ricevere prenotazioni.</p>
      <form onSubmit={submit} className="card-soft p-6 mt-8 space-y-4" data-testid="biz-setup-form">
        <Field label="Nome attività"><input required className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="biz-name" /></Field>
        <Field label="Categoria">
          <select className="input-soft" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="biz-category">
            {CATS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Città"><input required className="input-soft" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="biz-city" /></Field>
          <Field label="Telefono"><input className="input-soft" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="biz-phone" /></Field>
        </div>
        <Field label="Indirizzo"><input className="input-soft" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="biz-address" /></Field>
        <Field label="Descrizione"><textarea rows={3} className="input-soft h-auto py-3" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="biz-description" /></Field>
        <Field label="Immagine di copertina (URL)"><input className="input-soft" value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="biz-cover" /></Field>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <button disabled={loading} className="btn-accent w-full" data-testid="biz-create-submit">{loading ? "Creazione…" : "Crea attività"}</button>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  // Re-exported from the shared component to keep the shell working while
  // other panels still reference the local symbol. The implementation lives
  // in `./components/Field.jsx`.
  return <_Field label={label}>{children}</_Field>;
}

function Agenda({ businessId }) {
  const [items, setItems] = useState([]);
  const [staff, setStaff] = useState([]);
  const [hoursMap, setHoursMap] = useState({}); // { "0": {open, close, active, open2, close2}, ... }
  const [view, setView] = useState("day"); // list | day | week | month
  const [dayLayout, setDayLayout] = useState(() => localStorage.getItem("prenotoo_day_layout") || "standard"); // standard | compact | staff | grid
  const [hideCancelled, setHideCancelled] = useState(() => localStorage.getItem("prenotoo_hide_cancelled") === "1");
  const [hideNoShow, setHideNoShow] = useState(() => localStorage.getItem("prenotoo_hide_noshow") === "1");
  const [hideCompleted, setHideCompleted] = useState(() => localStorage.getItem("prenotoo_hide_completed") === "1");
  const [onlyConflicts, setOnlyConflicts] = useState(() => localStorage.getItem("prenotoo_only_conflicts") === "1");
  const [staffFilter, setStaffFilter] = useState(() => {
    try {
      const raw = localStorage.getItem("prenotoo_staff_filter");
      const arr = raw ? JSON.parse(raw) : [];
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
  const [selected, setSelected] = useState(null);
  const [createAt, setCreateAt] = useState(null);
  const [moveConfirm, setMoveConfirm] = useState(null);
  const [manualMoveFor, setManualMoveFor] = useState(null); // booking being manually rescheduled
  const [hardDeleteFor, setHardDeleteFor] = useState(null); // booking being permanently deleted
  const [toast, setToast] = useState("");
  const [previewBlock, setPreviewBlock] = useState(null); // { booking, x, y } for long-press preview
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [overrides, setOverrides] = useState([]);

  async function load() {
    // Each call is independently `.catch`'d so a transient 403 on any
    // sub-resource (e.g. a staff_user with reduced perms) does not blow up
    // the whole Agenda with the React error overlay.
    const [a, s, h, hol, ovr] = await Promise.all([
      api.get(`/bookings/business/${businessId}`).catch(() => ({ data: [] })),
      api.get(`/businesses/${businessId}/staff`).catch(() => ({ data: [] })),
      api.get(`/businesses/${businessId}/hours`).catch(() => ({ data: { hours: {} } })),
      api.get(`/businesses/${businessId}/holidays`).catch(() => ({ data: [] })),
      api.get(`/businesses/${businessId}/hours-overrides`).catch(() => ({ data: [] })),
    ]);
    setItems(a.data || []);
    setStaff(s.data || []);
    setHoursMap((h.data && h.data.hours) || {});
    setHolidays(hol.data || []);
    setOverrides(ovr.data || []);
  }
  useEffect(() => { load(); }, [businessId]);

  // Calendar overlay helpers — used by MiniMonthMap and MonthView.
  // `holidayFor(date)` returns the matching holiday (or null) considering ranges + recurring.
  // `overrideFor(date)` returns the matching override (or null).
  const overlay = useMemo(() => {
    function holidayFor(d) {
      const iso = ymdLocal(d);
      const md = iso.slice(5);
      for (const h of holidays) {
        if (h.date_to) {
          if (iso >= h.date && iso <= h.date_to) return h;
        } else if (h.date === iso) {
          return h;
        } else if (h.recurring && h.date.slice(5) === md) {
          return h;
        }
      }
      return null;
    }
    function overrideFor(d) {
      const iso = ymdLocal(d);
      const wk = (d.getDay() + 6) % 7;
      // Most recent first (mirror backend sort by created_at desc)
      const matches = overrides.filter((o) =>
        iso >= o.date_from && iso <= o.date_to && (o.active_dows || []).includes(wk),
      );
      if (matches.length === 0) return null;
      return matches.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
    }
    return { holidayFor, overrideFor };
  }, [holidays, overrides]);

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2800);
  }

  async function cancel(id) {
    try {
      await api.post(`/bookings/${id}/cancel`);
      showToast("Prenotazione annullata");
      setSelected(null);
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Impossibile annullare");
    }
  }

  async function noShow(id) {
    try {
      await api.post(`/bookings/${id}/no-show`);
      showToast("Segnato come no-show");
      setSelected(null);
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    }
  }

  async function checkIn(id) {
    try {
      await api.post(`/bookings/${id}/check-in`);
      showToast("Check-in registrato ✓");
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore check-in");
    }
  }

  async function complete(id) {
    try {
      await api.post(`/bookings/${id}/complete`);
      showToast("Appuntamento completato ✓");
      setSelected(null);
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    }
  }

  async function revertStatus(id) {
    try {
      await api.post(`/bookings/${id}/revert`);
      showToast("Stato ripristinato");
      setSelected(null);
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Errore");
    }
  }

  async function move(id, newStart, staffId = undefined) {
    // Open confirmation modal with "from → to" details
    const bk = items.find((b) => b.id === id);
    if (!bk) return;
    const origStart = new Date(bk.start_at);
    const origStaffName = bk.staff_name;
    const newStaffName = staffId !== undefined
      ? (staffId === null ? null : (staff.find((s) => s.id === staffId)?.name || null))
      : origStaffName;
    // Skip confirm if nothing changed
    const sameTime = Math.abs(newStart.getTime() - origStart.getTime()) < 60_000;
    const sameStaff = staffId === undefined || staffId === bk.staff_id;
    if (sameTime && sameStaff) return;
    setMoveConfirm({
      bookingId: id,
      serviceName: bk.service_name,
      customerName: bk.customer_name,
      fromDate: origStart,
      toDate: newStart,
      fromStaffName: origStaffName,
      toStaffName: newStaffName,
      staffId: staffId,
    });
  }

  async function doMove(info) {
    try {
      const payload = { start_at: toLocalIso(info.toDate) };
      if (info.staffId !== undefined) payload.staff_id = info.staffId;
      await api.put(`/bookings/${info.bookingId}/move`, payload);
      showToast("Appuntamento spostato ✓");
      setMoveConfirm(null);
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Impossibile spostare");
      setMoveConfirm(null);
    }
  }

  async function hardDelete(id) {
    try {
      await api.delete(`/bookings/${id}`);
      showToast("Prenotazione eliminata ✓");
      setSelected(null);
      setHardDeleteFor(null);
      load();
    } catch (e) {
      showToast(formatErr(e.response?.data?.detail) || "Impossibile eliminare");
      setHardDeleteFor(null);
    }
  }

  function shift(dir) {
    const d = new Date(cursor);
    if (view === "day" || view === "list") d.setDate(d.getDate() + dir);
    else if (view === "week") d.setDate(d.getDate() + dir * 7);
    else if (view === "month") d.setMonth(d.getMonth() + dir);
    d.setHours(0, 0, 0, 0);
    setCursor(d);
  }
  function today() { const d = new Date(); d.setHours(0,0,0,0); setCursor(d); }

  // Smart "new booking" — same logic as the "+ Nuovo" button, factored out for keyboard
  // shortcut reuse. Today → next 15min; otherwise → 10:00.
  function openNewBooking() {
    const base = new Date(cursor);
    const now = new Date();
    if (sameDay(cursor, now)) {
      const m15 = Math.ceil(now.getMinutes() / 15) * 15;
      base.setHours(now.getHours(), m15 === 60 ? 0 : m15, 0, 0);
      if (m15 === 60) base.setHours(base.getHours() + 1);
    } else {
      base.setHours(10, 0, 0, 0);
    }
    setCreateAt(base);
  }

  // Keyboard shortcuts (j/k = prev/next, t = today, 1-4 = view, n = new, ? = help, esc = close)
  useEffect(() => {
    function onKey(e) {
      // Ignore when typing in form fields
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Esc closes any open modal/preview
      if (e.key === "Escape") {
        if (selected) { setSelected(null); e.preventDefault(); return; }
        if (createAt) { setCreateAt(null); e.preventDefault(); return; }
        if (manualMoveFor) { setManualMoveFor(null); e.preventDefault(); return; }
        if (moveConfirm) { setMoveConfirm(null); e.preventDefault(); return; }
        if (hardDeleteFor) { setHardDeleteFor(null); e.preventDefault(); return; }
        if (previewBlock) { setPreviewBlock(null); e.preventDefault(); return; }
        if (showShortcuts) { setShowShortcuts(false); e.preventDefault(); return; }
        return;
      }
      // Don't trigger shortcuts when a modal is open
      if (selected || createAt || manualMoveFor || moveConfirm || hardDeleteFor) return;
      const k = e.key.toLowerCase();
      if (k === "j" || e.key === "ArrowRight") { shift(1); e.preventDefault(); }
      else if (k === "k" || e.key === "ArrowLeft") { shift(-1); e.preventDefault(); }
      else if (k === "t") { today(); e.preventDefault(); }
      else if (k === "1") { setView("list"); e.preventDefault(); }
      else if (k === "2") { setView("day"); e.preventDefault(); }
      else if (k === "3") { setView("week"); e.preventDefault(); }
      else if (k === "4") { setView("month"); e.preventDefault(); }
      else if (k === "n") { openNewBooking(); e.preventDefault(); }
      else if (e.key === "?") { setShowShortcuts((s) => !s); e.preventDefault(); }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line
  }, [view, cursor, selected, createAt, manualMoveFor, moveConfirm, hardDeleteFor, previewBlock, showShortcuts]);

  const colorOf = (b) => {
    if (b.status === "cancelled") return "#A1A1AA";
    if (b.status === "no_show") return "#F59E0B";
    if (b.status === "in_progress") return "#8B5CF6";
    if (b.status === "completed") return "#10B981";
    const s = staff.find((x) => x.id === b.staff_id);
    return s?.color || "#2563EB";
  };

  const upcomingCount = items.filter(b => new Date(b.start_at) >= new Date() && b.status === "confirmed").length;
  const cancelledCount = items.filter(b => b.status === "cancelled").length;
  const noShowCount = items.filter(b => b.status === "no_show").length;
  const completedCount = items.filter(b => b.status === "completed").length;
  const activeFiltersCount = (hideCancelled ? 1 : 0) + (hideNoShow ? 1 : 0) + (hideCompleted ? 1 : 0) + (onlyConflicts ? 1 : 0) + (staffFilter.size > 0 ? 1 : 0);

  // Apply all agenda filters before passing to views. KPI cards keep using `items`.
  const visibleItems = useMemo(() => {
    let out = items;
    if (hideCancelled) out = out.filter((b) => b.status !== "cancelled");
    if (hideNoShow) out = out.filter((b) => b.status !== "no_show");
    if (hideCompleted) out = out.filter((b) => b.status !== "completed");
    if (staffFilter.size > 0) {
      // "Senza staff" pseudo-id is the empty string ""
      out = out.filter((b) => staffFilter.has(b.staff_id || ""));
    }
    if (onlyConflicts) {
      // Conflicts are computed per day. Two non-cancelled bookings are in
      // conflict when their [start, end) intervals overlap.
      const byDay = new Map();
      for (const b of out) {
        if (b.status === "cancelled") continue;
        const d = ymdLocal(b.start_at);
        if (!byDay.has(d)) byDay.set(d, []);
        byDay.get(d).push(b);
      }
      const conflictIds = new Set();
      for (const list of byDay.values()) {
        for (let i = 0; i < list.length; i++) {
          const si = new Date(list[i].start_at).getTime();
          const ei = new Date(list[i].end_at).getTime();
          for (let j = i + 1; j < list.length; j++) {
            const sj = new Date(list[j].start_at).getTime();
            const ej = new Date(list[j].end_at).getTime();
            if (ei > sj && ej > si) {
              conflictIds.add(list[i].id);
              conflictIds.add(list[j].id);
            }
          }
        }
      }
      out = out.filter((b) => conflictIds.has(b.id));
    }
    return out;
  }, [items, hideCancelled, hideNoShow, hideCompleted, onlyConflicts, staffFilter]);

  // Click-outside to close the filters dropdown
  useEffect(() => {
    if (!filtersOpen) return;
    function onDocClick(e) {
      if (!e.target.closest('[data-testid="agenda-filters-popover"]') && !e.target.closest('[data-testid="agenda-filters-btn"]')) {
        setFiltersOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [filtersOpen]);

  const rangeLabel = useMemo(() => {
    if (view === "month") return cursor.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
    if (view === "week") {
      const start = startOfWeek(cursor); const end = new Date(start); end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })} – ${end.toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}`;
    }
    return cursor.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" });
  }, [view, cursor]);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Totali" value={items.length} />
        <Stat label="Prossime" value={upcomingCount} />
        <Stat label="Cancellate" value={items.filter(b => b.status === "cancelled").length} />
        <Stat label="Oggi" value={items.filter(b => ymdLocal(b.start_at) === ymdLocal(new Date())).length} />
      </div>

      {/* View toggle + nav */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="inline-flex p-1 bg-zinc-100 rounded-full self-start dark:bg-zinc-800" data-testid="agenda-view-toggle">
          {[
            { k: "list", label: "Lista" },
            { k: "day", label: "Giorno" },
            { k: "week", label: "Settimana" },
            { k: "month", label: "Mese" },
          ].map((v) => (
            <button
              key={v.k}
              onClick={() => setView(v.k)}
              className={`h-9 px-4 rounded-full text-sm font-medium transition ${view === v.k ? "bg-white shadow text-zinc-900 dark:bg-zinc-900 dark:text-zinc-100" : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-100"}`}
              data-testid={`agenda-view-${v.k}`}
            >
              {v.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => shift(-1)} className="h-10 w-10 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="agenda-prev" aria-label="Precedente">‹</button>
          <button onClick={today} className="btn-secondary h-10 px-4 text-sm" data-testid="agenda-today">Oggi</button>
          <button onClick={() => shift(1)} className="h-10 w-10 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="agenda-next" aria-label="Successivo">›</button>
          <button
            onClick={openNewBooking}
            className="btn-accent h-10 px-4 text-sm ml-1"
            data-testid="agenda-new"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Nuovo
          </button>
          <button
            onClick={() => setShowShortcuts(true)}
            className="hidden md:inline-flex h-10 w-10 rounded-full bg-zinc-100 hover:bg-zinc-200 items-center justify-center text-sm font-semibold dark:bg-zinc-800 dark:hover:bg-zinc-700"
            title="Scorciatoie tastiera (?)"
            data-testid="agenda-shortcuts-btn"
          >
            ?
          </button>
          <div className="relative" data-testid="agenda-filters-popover">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={`hidden md:inline-flex h-10 px-3 rounded-full text-xs font-medium border items-center gap-1.5 transition ${
                activeFiltersCount > 0
                  ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
                  : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"
              }`}
              title="Filtri agenda"
              data-testid="agenda-filters-btn"
              aria-expanded={filtersOpen}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              <span>Filtri</span>
              {activeFiltersCount > 0 && (
                <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums ${activeFiltersCount > 0 ? "bg-white/20 text-white dark:bg-zinc-900/15 dark:text-zinc-900" : "bg-zinc-100 text-zinc-700"}`}>{activeFiltersCount}</span>
              )}
            </button>
            {filtersOpen && (
              <div
                className="absolute right-0 mt-2 w-72 rounded-2xl border border-zinc-200 bg-white shadow-xl p-2 z-30 dark:bg-zinc-900 dark:border-zinc-700"
                role="menu"
                data-testid="agenda-filters-menu"
              >
                <div className="px-3 py-2 text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 dark:text-zinc-500">Filtri agenda</div>
                <FilterRow
                  testid="filter-hide-cancelled"
                  label="Nascondi cancellati"
                  count={cancelledCount}
                  countTone="zinc"
                  active={hideCancelled}
                  onToggle={() => {
                    const next = !hideCancelled;
                    setHideCancelled(next);
                    localStorage.setItem("prenotoo_hide_cancelled", next ? "1" : "0");
                  }}
                />
                <FilterRow
                  testid="filter-hide-noshow"
                  label="Nascondi no-show"
                  count={noShowCount}
                  countTone="amber"
                  active={hideNoShow}
                  onToggle={() => {
                    const next = !hideNoShow;
                    setHideNoShow(next);
                    localStorage.setItem("prenotoo_hide_noshow", next ? "1" : "0");
                  }}
                />
                <FilterRow
                  testid="filter-hide-completed"
                  label="Nascondi completati"
                  count={completedCount}
                  countTone="emerald"
                  active={hideCompleted}
                  onToggle={() => {
                    const next = !hideCompleted;
                    setHideCompleted(next);
                    localStorage.setItem("prenotoo_hide_completed", next ? "1" : "0");
                  }}
                />
                <div className="my-1 mx-3 border-t border-zinc-100 dark:border-zinc-800" />
                <FilterRow
                  testid="filter-only-conflicts"
                  label="Mostra solo conflitti"
                  hint="Solo prenotazioni che si sovrappongono"
                  active={onlyConflicts}
                  onToggle={() => {
                    const next = !onlyConflicts;
                    setOnlyConflicts(next);
                    localStorage.setItem("prenotoo_only_conflicts", next ? "1" : "0");
                  }}
                />
                {staff.length > 0 && (
                  <>
                    <div className="my-1 mx-3 border-t border-zinc-100 dark:border-zinc-800" />
                    <div className="px-3 pt-2 pb-1 flex items-center justify-between">
                      <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-400 dark:text-zinc-500">Staff</span>
                      {staffFilter.size > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            setStaffFilter(new Set());
                            localStorage.removeItem("prenotoo_staff_filter");
                          }}
                          className="text-[10px] text-blue-600 hover:underline"
                          data-testid="filter-staff-clear"
                        >
                          Tutti
                        </button>
                      )}
                    </div>
                    <div className="px-3 pb-2 flex flex-wrap gap-1.5" data-testid="filter-staff-chips">
                      {staff.map((s) => {
                        const active = staffFilter.has(s.id);
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => {
                              const next = new Set(staffFilter);
                              if (active) next.delete(s.id); else next.add(s.id);
                              setStaffFilter(next);
                              if (next.size === 0) localStorage.removeItem("prenotoo_staff_filter");
                              else localStorage.setItem("prenotoo_staff_filter", JSON.stringify(Array.from(next)));
                            }}
                            className={`h-7 px-2.5 rounded-full text-[11px] font-medium border inline-flex items-center gap-1.5 transition ${
                              active
                                ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
                                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"
                            }`}
                            data-testid={`filter-staff-${s.id}`}
                          >
                            <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: s.color || "#2563EB" }} aria-hidden />
                            <span className="truncate max-w-[120px]">{s.name}</span>
                          </button>
                        );
                      })}
                      {(() => {
                        const hasUnassigned = items.some((b) => !b.staff_id);
                        if (!hasUnassigned) return null;
                        const active = staffFilter.has("");
                        return (
                          <button
                            type="button"
                            onClick={() => {
                              const next = new Set(staffFilter);
                              if (active) next.delete(""); else next.add("");
                              setStaffFilter(next);
                              if (next.size === 0) localStorage.removeItem("prenotoo_staff_filter");
                              else localStorage.setItem("prenotoo_staff_filter", JSON.stringify(Array.from(next)));
                            }}
                            className={`h-7 px-2.5 rounded-full text-[11px] font-medium border inline-flex items-center gap-1.5 transition ${
                              active
                                ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100"
                                : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"
                            }`}
                            data-testid="filter-staff-unassigned"
                          >
                            <span className="h-2 w-2 rounded-full shrink-0 bg-zinc-400" aria-hidden />
                            <span>Senza staff</span>
                          </button>
                        );
                      })()}
                    </div>
                  </>
                )}
                {activeFiltersCount > 0 && (
                  <div className="px-3 py-2 mt-1 border-t border-zinc-100 dark:border-zinc-800">
                    <button
                      type="button"
                      onClick={() => {
                        setHideCancelled(false); localStorage.setItem("prenotoo_hide_cancelled", "0");
                        setHideNoShow(false); localStorage.setItem("prenotoo_hide_noshow", "0");
                        setHideCompleted(false); localStorage.setItem("prenotoo_hide_completed", "0");
                        setOnlyConflicts(false); localStorage.setItem("prenotoo_only_conflicts", "0");
                        setStaffFilter(new Set()); localStorage.removeItem("prenotoo_staff_filter");
                      }}
                      className="text-xs text-blue-600 hover:underline"
                      data-testid="filter-clear-all"
                    >
                      Cancella tutti i filtri
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ml-3 font-display text-lg capitalize hidden md:block">{rangeLabel}</div>
        </div>
      </div>
      <div className="font-display text-lg capitalize md:hidden mb-3">{rangeLabel}</div>

      {view === "day" && (
        <div className="flex items-center justify-end gap-1 mb-3" data-testid="day-layout-switcher">
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold mr-2 hidden sm:inline dark:text-zinc-500">Layout</span>
          {[
            { k: "standard", label: "Standard", icon: "▭" },
            { k: "compact", label: "Compatta", icon: "≡" },
            { k: "staff", label: "Staff", icon: "▥" },
            { k: "grid", label: "Griglia", icon: "▦" },
          ].map((l) => (
            <button
              key={l.k}
              onClick={() => { setDayLayout(l.k); localStorage.setItem("prenotoo_day_layout", l.k); }}
              className={`h-8 px-3 text-xs rounded-full font-medium transition border ${dayLayout === l.k ? "bg-zinc-900 text-white border-zinc-900 dark:bg-zinc-100 dark:text-zinc-900 dark:border-zinc-100" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 dark:bg-zinc-900 dark:text-zinc-300 dark:border-zinc-700 dark:hover:border-zinc-500"}`}
              title={l.label}
              data-testid={`day-layout-${l.k}`}
            >
              <span className="mr-1">{l.icon}</span>{l.label}
            </button>
          ))}
        </div>
      )}

      {/* Active filters chip-bar — visible only when ≥1 filter is on. Click ✕ to remove the specific filter. */}
      {activeFiltersCount > 0 && (
        <div
          className="flex items-center gap-2 flex-wrap mb-3 px-1"
          data-testid="active-filters-chipbar"
        >
          <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold dark:text-zinc-500 mr-1">Filtri attivi</span>
          {hideCancelled && (
            <ActiveFilterChip
              testid="chip-hide-cancelled"
              label="Cancellati nascosti"
              onRemove={() => { setHideCancelled(false); localStorage.setItem("prenotoo_hide_cancelled", "0"); }}
            />
          )}
          {hideNoShow && (
            <ActiveFilterChip
              testid="chip-hide-noshow"
              label="No-show nascosti"
              onRemove={() => { setHideNoShow(false); localStorage.setItem("prenotoo_hide_noshow", "0"); }}
            />
          )}
          {hideCompleted && (
            <ActiveFilterChip
              testid="chip-hide-completed"
              label="Completati nascosti"
              onRemove={() => { setHideCompleted(false); localStorage.setItem("prenotoo_hide_completed", "0"); }}
            />
          )}
          {onlyConflicts && (
            <ActiveFilterChip
              testid="chip-only-conflicts"
              label="Solo conflitti"
              onRemove={() => { setOnlyConflicts(false); localStorage.setItem("prenotoo_only_conflicts", "0"); }}
            />
          )}
          {Array.from(staffFilter).map((sid) => {
            const isUnassigned = sid === "";
            const s = isUnassigned ? null : staff.find((x) => x.id === sid);
            const label = isUnassigned ? "Senza staff" : (s?.name || "Staff");
            const dot = isUnassigned ? "#A1A1AA" : (s?.color || "#2563EB");
            return (
              <ActiveFilterChip
                key={`staff-${sid || "none"}`}
                testid={`chip-staff-${sid || "unassigned"}`}
                label={label}
                dot={dot}
                onRemove={() => {
                  const next = new Set(staffFilter);
                  next.delete(sid);
                  setStaffFilter(next);
                  if (next.size === 0) localStorage.removeItem("prenotoo_staff_filter");
                  else localStorage.setItem("prenotoo_staff_filter", JSON.stringify(Array.from(next)));
                }}
              />
            );
          })}
          <button
            type="button"
            onClick={() => {
              setHideCancelled(false); localStorage.setItem("prenotoo_hide_cancelled", "0");
              setHideNoShow(false); localStorage.setItem("prenotoo_hide_noshow", "0");
              setHideCompleted(false); localStorage.setItem("prenotoo_hide_completed", "0");
              setOnlyConflicts(false); localStorage.setItem("prenotoo_only_conflicts", "0");
              setStaffFilter(new Set()); localStorage.removeItem("prenotoo_staff_filter");
            }}
            className="text-[11px] text-blue-600 hover:underline ml-1"
            data-testid="chipbar-clear-all"
          >
            Cancella tutti
          </button>
        </div>
      )}

      {view === "list" && <ListView items={visibleItems} cursor={cursor} staff={staff} onSelect={setSelected} cancel={cancel} noShow={noShow} checkIn={checkIn} complete={complete} />}
      {view === "day" && (
        dayLayout === "staff" ? (
          <DayViewStaff items={visibleItems} cursor={cursor} staff={staff} hoursMap={hoursMap} colorOf={colorOf} onSelect={setSelected} onMove={move} onCreateAt={(d) => setCreateAt(d)} />
        ) : dayLayout === "grid" ? (
          <DayViewGrid items={visibleItems} cursor={cursor} staff={staff} hoursMap={hoursMap} colorOf={colorOf} onSelect={setSelected} />
        ) : (
          <DayView items={visibleItems} cursor={cursor} staff={staff} hoursMap={hoursMap} colorOf={colorOf} onSelect={setSelected} onMove={move} onCreateAt={setCreateAt} density={dayLayout} onPreview={(b, e) => setPreviewBlock({ booking: b, x: e.clientX, y: e.clientY })} />
        )
      )}
      {view === "week" && <WeekView items={visibleItems} cursor={cursor} staff={staff} hoursMap={hoursMap} colorOf={colorOf} onSelect={setSelected} onPickDay={(d) => { setCursor(d); setView("day"); }} onMove={move} onCreateAt={setCreateAt} />}
      {view === "month" && <MonthView items={visibleItems} cursor={cursor} staff={staff} colorOf={colorOf} overlay={overlay} onPickDay={(d) => { setCursor(d); setView("day"); }} />}

      {/* Staff legend (only in Week/Month — Day view has its own revenue footer with states) */}
      {staff.length > 0 && view !== "list" && view !== "day" && (
        <div className="mt-5 flex flex-wrap gap-3 text-xs text-zinc-600 dark:text-zinc-400">
          {staff.map((s) => (
            <span key={s.id} className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color || "#2563EB" }} />
              {s.name}
            </span>
          ))}
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-blue-600" />Senza staff</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-500" />No-show</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-zinc-400" />Annullata</span>
        </div>
      )}

      {/* Booking detail popover */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-4" onClick={() => setSelected(null)} data-testid="agenda-detail">
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md card-soft p-6 dark:bg-zinc-900">
            <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">{new Date(selected.start_at).toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "long" })}</div>
            <div className="font-display text-2xl font-medium mt-1">{selected.service_name}</div>
            <div className="mt-3 text-sm text-zinc-700 space-y-1 dark:text-zinc-300">
              <div><span className="text-zinc-500 dark:text-zinc-400">Cliente:</span> {selected.customer_name}</div>
              <div><span className="text-zinc-500 dark:text-zinc-400">Orario:</span> {new Date(selected.start_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} – {new Date(selected.end_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
              {selected.staff_name && <div><span className="text-zinc-500 dark:text-zinc-400">Operatore:</span> {selected.staff_name}</div>}
              <div><span className="text-zinc-500 dark:text-zinc-400">Prezzo:</span> €{selected.price}</div>
              <div>
                <span className="text-zinc-500 dark:text-zinc-400">Stato:</span>{" "}
                <span className={
                  selected.status === "cancelled" ? "text-red-500" :
                  selected.status === "no_show" ? "text-amber-600" :
                  selected.status === "in_progress" ? "text-violet-600 font-semibold" :
                  selected.status === "completed" ? "text-emerald-700 font-semibold" :
                  "text-emerald-600"
                }>
                  {({
                    confirmed: "Confermato",
                    in_progress: "In corso",
                    completed: "Completato",
                    cancelled: "Annullato",
                    no_show: "No-show",
                  })[selected.status] || selected.status}
                </span>
              </div>
              {selected.checked_in_at && (
                <div><span className="text-zinc-500 dark:text-zinc-400">Check-in:</span> {new Date(selected.checked_in_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
              )}
              {selected.completed_at && selected.checked_in_at && (
                <div><span className="text-zinc-500 dark:text-zinc-400">Durata reale:</span> {Math.round((new Date(selected.completed_at) - new Date(selected.checked_in_at)) / 60000)} min</div>
              )}
            </div>
            {selected.status === "confirmed" && (
              <div className="space-y-2 mt-5">
                <div className="flex gap-2">
                  <button onClick={() => checkIn(selected.id)} className="flex-1 h-11 rounded-full bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium inline-flex items-center justify-center gap-1" data-testid="detail-checkin">
                    ▶ Arrivato
                  </button>
                  <button onClick={() => complete(selected.id)} className="flex-1 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center justify-center gap-1" data-testid="detail-complete">
                    ✓ Completato
                  </button>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => noShow(selected.id)} className="flex-1 h-11 rounded-full border border-amber-300 text-amber-700 hover:bg-amber-50 text-sm font-medium dark:text-amber-300 dark:bg-amber-950/40" data-testid="detail-noshow">⊘ Non arrivato</button>
                  <button onClick={() => cancel(selected.id)} className="flex-1 h-11 rounded-full border border-red-300 text-red-700 hover:bg-red-50 text-sm font-medium dark:text-red-300 dark:bg-red-950/40" data-testid="detail-cancel">Annulla</button>
                </div>
              </div>
            )}
            {selected.status === "in_progress" && (
              <div className="flex gap-2 mt-5">
                <button onClick={() => complete(selected.id)} className="flex-1 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium" data-testid="detail-complete">✓ Completa</button>
                <button onClick={() => revertStatus(selected.id)} className="h-11 px-4 rounded-full border border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-sm font-medium dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800" data-testid="detail-revert">Annulla check-in</button>
              </div>
            )}
            {(selected.status === "completed" || selected.status === "no_show") && (
              <div className="mt-5">
                <button onClick={() => revertStatus(selected.id)} className="w-full h-11 rounded-full border border-zinc-300 text-zinc-700 hover:bg-zinc-50 text-sm font-medium dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800" data-testid="detail-revert">Ripristina a Confermato</button>
              </div>
            )}
            <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800">
              {selected.status !== "cancelled" && (
                <button
                  onClick={() => setManualMoveFor(selected)}
                  className="flex-1 h-11 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5"
                  data-testid="detail-move-manual"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                  Sposta
                </button>
              )}
              <button
                onClick={() => setHardDeleteFor(selected)}
                className="flex-1 h-11 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium inline-flex items-center justify-center gap-1.5"
                data-testid="detail-delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V3a2 2 0 012-2h2a2 2 0 012 2v4"/></svg>
                Elimina
              </button>
            </div>
            <button onClick={() => setSelected(null)} className="w-full mt-3 text-sm text-zinc-500 dark:text-zinc-400" data-testid="detail-close">Chiudi</button>
          </div>
        </div>
      )}

      {createAt && (
        <ManualBookingModal
          businessId={businessId}
          startAt={createAt}
          staff={staff}
          onClose={() => setCreateAt(null)}
          onCreated={() => { setCreateAt(null); showToast("Appuntamento creato ✓"); load(); }}
        />
      )}

      {moveConfirm && (
        <MoveConfirmModal
          info={moveConfirm}
          onCancel={() => setMoveConfirm(null)}
          onConfirm={() => doMove(moveConfirm)}
        />
      )}

      {manualMoveFor && (
        <ManualMoveModal
          booking={manualMoveFor}
          onCancel={() => setManualMoveFor(null)}
          onConfirm={(newDate) => {
            setManualMoveFor(null);
            // reuse existing move() which opens the MoveConfirmModal for final confirm
            move(manualMoveFor.id, newDate);
            setSelected(null);
          }}
        />
      )}

      {hardDeleteFor && (
        <HardDeleteModal
          booking={hardDeleteFor}
          onCancel={() => setHardDeleteFor(null)}
          onConfirm={() => hardDelete(hardDeleteFor.id)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] bg-zinc-900 text-white text-sm px-4 py-2.5 rounded-full shadow-lg" data-testid="agenda-toast">
          {toast}
        </div>
      )}

      {/* Mini-mappa mese (solo desktop, viste day/week) */}
      {(view === "day" || view === "week") && (
        <MiniMonthMap
          cursor={cursor}
          items={items}
          overlay={overlay}
          onPick={(d) => { setCursor(d); if (view !== "day") setView("day"); }}
          data-testid="mini-month-map"
        />
      )}

      {/* Long-press preview popover — hidden during drag and during move confirmation */}
      {previewBlock && !moveConfirm && (
        <BlockPreview
          booking={previewBlock.booking}
          x={previewBlock.x}
          y={previewBlock.y}
          onClose={() => setPreviewBlock(null)}
        />
      )}

      {/* Keyboard shortcuts help */}
      {showShortcuts && (
        <ShortcutsHelp onClose={() => setShowShortcuts(false)} />
      )}
    </div>
  );
}

function startOfWeek(d) { const x = new Date(d); x.setHours(0,0,0,0); const wk = (x.getDay() + 6) % 7; x.setDate(x.getDate() - wk); return x; }
function sameDay(a, b) { return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate(); }
function mins(d) { return d.getHours() * 60 + d.getMinutes(); }
// Local YMD (NOT UTC-based): safe for comparisons against booking dates displayed locally
function ymdLocal(d) {
  const x = d instanceof Date ? d : new Date(d);
  const p = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}
// ISO 8601 string with local timezone offset. Avoids off-by-one issues when sending
// timestamps to the backend (which stores in UTC but correctly parses any tz-aware iso).
function toLocalIso(d) {
  const pad = (n) => String(n).padStart(2, "0");
  const tz = -d.getTimezoneOffset();
  const sign = tz >= 0 ? "+" : "-";
  const tzH = pad(Math.floor(Math.abs(tz) / 60));
  const tzM = pad(Math.abs(tz) % 60);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}${sign}${tzH}:${tzM}`;
}

function ListView({ items, cursor, staff, onSelect, cancel, noShow, checkIn, complete }) {
  const dayStr = ymdLocal(cursor);
  const filtered = items.filter((b) => ymdLocal(b.start_at) === dayStr).sort((a, b) => a.start_at.localeCompare(b.start_at));
  if (filtered.length === 0) return <div className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="agenda-empty">Nessuna prenotazione in questa data.</div>;
  const now = new Date();
  return (
    <div className="space-y-3">
      {filtered.map((b) => {
        const col = staff.find((s) => s.id === b.staff_id)?.color || "#2563EB";
        const statusBg = b.status === "cancelled" ? "#A1A1AA" : b.status === "no_show" ? "#F59E0B" : b.status === "in_progress" ? "#8B5CF6" : b.status === "completed" ? "#10B981" : col;
        // Needs attention: confirmed + start was more than 15 min ago + no check-in
        const startDt = new Date(b.start_at);
        const needsAttention = b.status === "confirmed" && (now - startDt) / 60000 > 15;
        const statusLabel = ({
          confirmed: "Confermato",
          in_progress: "In corso",
          completed: "Completato",
          cancelled: "Annullato",
          no_show: "No-show",
        })[b.status] || b.status;
        return (
          <div key={b.id} onClick={() => onSelect(b)} className={`card-soft p-5 flex items-center gap-4 cursor-pointer ${needsAttention ? "ring-2 ring-amber-400" : ""}`} data-testid={`agenda-booking-${b.id}`}>
            <div className="h-14 w-14 rounded-2xl flex flex-col items-center justify-center text-white" style={{ backgroundColor: statusBg }}>
              <span className="text-[10px] uppercase opacity-70">{new Date(b.start_at).toLocaleDateString("it-IT", { month: "short" })}</span>
              <span className="font-display text-xl leading-none">{new Date(b.start_at).getDate()}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2">
                {b.service_name} · {b.customer_name}
                {needsAttention && (
                  <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold dark:bg-amber-950/40 dark:text-amber-300" data-testid={`needs-attention-${b.id}`}>Da gestire</span>
                )}
                {b.overbooked && (
                  <span className="text-[10px] bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full font-semibold dark:bg-rose-950/40 dark:text-rose-300" data-testid={`overbooked-${b.id}`}>⚠ Overbooking</span>
                )}
              </div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                {new Date(b.start_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })} – {new Date(b.end_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                {b.staff_name && <span> · <span className="font-medium text-zinc-700 dark:text-zinc-300">{b.staff_name}</span></span>}
              </div>
            </div>
            <div className="text-right" onClick={(e) => e.stopPropagation()}>
              <div className="text-sm font-medium">€{b.price}</div>
              <div className={`text-xs ${b.status === "cancelled" ? "text-red-500" : b.status === "no_show" ? "text-amber-600" : b.status === "in_progress" ? "text-violet-600" : b.status === "completed" ? "text-emerald-700 dark:text-emerald-300" : "text-emerald-600"}`}>{statusLabel}</div>
              {b.status === "confirmed" && (
                <div className="flex gap-1.5 justify-end mt-1.5 flex-wrap">
                  <button onClick={() => checkIn(b.id)} className="text-[11px] bg-violet-600 text-white hover:bg-violet-700 px-2 py-1 rounded-full" data-testid={`checkin-booking-${b.id}`}>▶ Arrivato</button>
                  <button onClick={() => complete(b.id)} className="text-[11px] bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1 rounded-full" data-testid={`complete-booking-${b.id}`}>✓ Completato</button>
                  <button onClick={() => noShow(b.id)} className="text-[11px] text-amber-600 hover:text-amber-800 dark:text-amber-300" data-testid={`noshow-booking-${b.id}`}>No-show</button>
                </div>
              )}
              {b.status === "in_progress" && (
                <div className="flex gap-1.5 justify-end mt-1.5">
                  <button onClick={() => complete(b.id)} className="text-[11px] bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-1 rounded-full" data-testid={`complete-booking-${b.id}`}>✓ Completa</button>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const HOUR_PX_DENSITY = { standard: 60, compact: 30 };
const HOUR_PX = 60; // legacy default (still used in a couple of helpers)
const DEFAULT_START_HOUR = 9;
const DEFAULT_END_HOUR = 19;

// Maps weekday(0=Sun..6=Sat) -> working_hours key ("0"=Mon..."6"=Sun)
function whKey(date) {
  return String((date.getDay() + 6) % 7);
}

// Returns {active, open, close, open2, close2, startH, endH, hasBreak, breakStart, breakEnd}
// where open/close are minutes, startH/endH are rounded hours for the timeline grid.
function extendedRange(range, items) {
  // Extend start/end hours to include any out-of-hours bookings on the day (owner manual).
  let startH = range.active ? range.startH : DEFAULT_START_HOUR;
  let endH = range.active ? range.endH : DEFAULT_END_HOUR;
  for (const b of items) {
    if (b.status === "cancelled") continue;
    const s = new Date(b.start_at);
    const e = new Date(b.end_at);
    const sh = s.getHours() + s.getMinutes() / 60;
    const eh = e.getHours() + e.getMinutes() / 60;
    if (sh < startH) startH = Math.max(0, Math.floor(sh));
    if (eh > endH) endH = Math.min(24, Math.ceil(eh));
  }
  return { startH, endH };
}


function getDayRange(hoursMap, date) {
  const h = hoursMap?.[whKey(date)];
  if (!h || !h.active) return { active: false };
  const opens = [];
  const closes = [];
  if (h.open != null && h.close != null) { opens.push(h.open); closes.push(h.close); }
  if (h.open2 != null && h.close2 != null) { opens.push(h.open2); closes.push(h.close2); }
  if (opens.length === 0) return { active: false };
  const minOpen = Math.min(...opens);
  const maxClose = Math.max(...closes);
  const startH = Math.floor(minOpen / 60);
  const endH = Math.ceil(maxClose / 60);
  const hasBreak = opens.length === 2 && Math.max(h.close, h.open2) <= Math.min(h.close, h.open2) + 24 * 60 && h.open2 > h.close;
  return {
    active: true,
    open: h.open, close: h.close, open2: h.open2, close2: h.close2,
    startH, endH,
    hasBreak,
    breakStart: hasBreak ? Math.min(h.close, h.open2) : null,
    breakEnd: hasBreak ? Math.max(h.close, h.open2) : null,
  };
}

// Returns {startH, endH} covering all active days in the list, or defaults if none.
function getRangeForDays(hoursMap, dates) {
  let startH = 24, endH = 0;
  let any = false;
  dates.forEach((d) => {
    const r = getDayRange(hoursMap, d);
    if (r.active) {
      any = true;
      if (r.startH < startH) startH = r.startH;
      if (r.endH > endH) endH = r.endH;
    }
  });
  if (!any) return { startH: DEFAULT_START_HOUR, endH: DEFAULT_END_HOUR };
  return { startH, endH };
}

function TimelineBg({ startH, endH, hourPx = HOUR_PX }) {
  const rows = [];
  for (let h = startH; h < endH; h++) {
    rows.push(
      <div key={h} style={{ height: hourPx }} className="border-t border-zinc-100 relative dark:border-zinc-800">
        <span className="absolute -top-2 left-1 text-[10px] text-zinc-400 font-medium bg-white pr-1 dark:text-zinc-500 dark:bg-zinc-900">{String(h).padStart(2, "0")}:00</span>
      </div>
    );
  }
  return <div className="relative">{rows}</div>;
}

/**
 * Compute lane (column) layout for a set of time-blocks so overlapping ones
 * sit side-by-side horizontally instead of stacking on top of each other.
 *
 * Returns a map: { [block.id]: { lane: number, lanes: number } }
 * - `lane`  → 0-indexed column for this block within its overlap cluster
 * - `lanes` → total columns in this block's cluster
 *
 * NOTE: Cancelled bookings are INCLUDED in the lane calculation so they
 * don't visually overlap (and obscure click targets of) the active bookings
 * that share the same time slot. Their translucent style already
 * distinguishes them from confirmed ones.
 */
function computeLanes(blocks) {
  const result = new Map();
  const sortable = blocks
    .map((b) => ({
      id: b.id,
      s: new Date(b.start_at).getTime(),
      e: new Date(b.end_at).getTime(),
    }))
    .sort((a, b) => a.s - b.s || a.e - b.e);
  let cluster = []; // active blocks in current overlap cluster
  let lanes = []; // for each lane index, the latest end-time
  function flush() {
    const total = lanes.length || 1;
    cluster.forEach(({ id, lane }) => result.set(id, { lane, lanes: total }));
    cluster = [];
    lanes = [];
  }
  for (const blk of sortable) {
    // If the block starts after all current lanes are done → flush cluster
    if (lanes.length && lanes.every((endTs) => endTs <= blk.s)) flush();
    // Find first free lane whose latest end ≤ blk.s
    let lane = lanes.findIndex((endTs) => endTs <= blk.s);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(blk.e);
    } else {
      lanes[lane] = blk.e;
    }
    cluster.push({ id: blk.id, lane });
  }
  flush();
  // Safety fallback: any block somehow missed → render full width
  blocks.forEach((b) => {
    if (!result.has(b.id)) result.set(b.id, { lane: 0, lanes: 1 });
  });
  return result;
}

function DayView({ items, cursor, hoursMap, onSelect, colorOf, onMove, onCreateAt, density = "standard", onPreview }) {
  const hourPx = HOUR_PX_DENSITY[density] || HOUR_PX;
  const dayStr = ymdLocal(cursor);
  const todaysItems = items.filter((b) => ymdLocal(b.start_at) === dayStr);
  const range = getDayRange(hoursMap, cursor);
  const hasOutOfHours = todaysItems.some((b) => b.status !== "cancelled");
  // Extend timeline to include any out-of-hours manual bookings
  const ext = hasOutOfHours ? extendedRange(range, todaysItems) : { startH: range.active ? range.startH : DEFAULT_START_HOUR, endH: range.active ? range.endH : DEFAULT_END_HOUR };
  const startH = ext.startH;
  const endH = ext.endH;
  const timelineHeight = (endH - startH) * hourPx;

  if (!range.active && todaysItems.length === 0) {
    return (
      <div className="card-soft p-10 text-center" data-testid="day-closed">
        <div className="font-display text-xl font-medium">Chiuso</div>
        <div className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">L'attività non è aperta questo giorno. Configura gli orari in "Orari".</div>
      </div>
    );
  }

  function handleDrop(e) {
    e.preventDefault();
    const id = e.dataTransfer.getData("booking-id");
    if (!id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top - 8;
    let minutesFromStart = (offsetY / hourPx) * 60;
    minutesFromStart = Math.max(0, Math.round(minutesFromStart / 15) * 15);
    const newStart = new Date(cursor);
    newStart.setHours(startH, 0, 0, 0);
    newStart.setMinutes(newStart.getMinutes() + minutesFromStart);
    onMove(id, newStart);
  }

  function handleClickEmpty(e) {
    if (e.target.dataset.block) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top - 8;
    let minutesFromStart = (offsetY / hourPx) * 60;
    minutesFromStart = Math.max(0, Math.round(minutesFromStart / 15) * 15);
    const newStart = new Date(cursor);
    newStart.setHours(startH, 0, 0, 0);
    newStart.setMinutes(newStart.getMinutes() + minutesFromStart);
    onCreateAt(newStart);
  }

  const breakTop = range.hasBreak ? ((range.breakStart - startH * 60) / 60) * hourPx + 8 : 0;
  const breakHeight = range.hasBreak ? ((range.breakEnd - range.breakStart) / 60) * hourPx : 0;
  const isCompact = density === "compact";

  return (
    <div className="card-soft overflow-hidden" data-testid={`day-view-${density}`}>
      <div className="flex">
        <div className="w-14 shrink-0 pt-2 border-r border-zinc-100 dark:border-zinc-800" style={{ height: timelineHeight + 16 }}>
          <TimelineBg startH={startH} endH={endH} hourPx={hourPx} />
        </div>
        <div
          className="flex-1 relative pt-2 cursor-copy"
          style={{ height: timelineHeight + 16 }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          onClick={handleClickEmpty}
          data-testid="day-timeline"
        >
          <div className="absolute inset-0 pt-2">
            {Array.from({ length: endH - startH }).map((_, i) => (
              <div key={i} style={{ height: hourPx }} className="border-t border-zinc-50 dark:border-zinc-800/60" />
            ))}
          </div>
          {range.hasBreak && (
            <div
              className="absolute left-0 right-0 pointer-events-none"
              style={{
                top: breakTop,
                height: breakHeight,
                background: "repeating-linear-gradient(45deg, rgba(244,244,245,0.9) 0 8px, rgba(228,228,231,0.9) 8px 16px)",
              }}
              data-testid="day-break"
            >
              <span className="absolute top-1 left-2 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Pausa</span>
            </div>
          )}
          {sameDay(cursor, new Date()) && <NowLine startH={startH} endH={endH} hourPx={hourPx} />}
          {(() => {
            // Compute overlap conflicts: for each block, find others that overlap
            const overlapIds = new Set();
            for (let i = 0; i < todaysItems.length; i++) {
              if (todaysItems[i].status === "cancelled") continue;
              const ai_s = new Date(todaysItems[i].start_at).getTime();
              const ai_e = new Date(todaysItems[i].end_at).getTime();
              for (let j = i + 1; j < todaysItems.length; j++) {
                if (todaysItems[j].status === "cancelled") continue;
                const aj_s = new Date(todaysItems[j].start_at).getTime();
                const aj_e = new Date(todaysItems[j].end_at).getTime();
                if (!(ai_e <= aj_s || aj_e <= ai_s)) {
                  overlapIds.add(todaysItems[i].id);
                  overlapIds.add(todaysItems[j].id);
                }
              }
            }
            // Compute horizontal lane layout (Google-Calendar style) so overlapping
            // blocks sit side-by-side and remain individually clickable.
            const laneMap = computeLanes(todaysItems);
            return todaysItems.map((b) => {
              const start = new Date(b.start_at);
              const end = new Date(b.end_at);
              const top = ((mins(start) - startH * 60) / 60) * hourPx + 8;
              const height = Math.max(isCompact ? 16 : 22, ((end - start) / 60000 / 60) * hourPx - 2);
              const bg = colorOf(b);
              const translucent = b.status !== "confirmed";
              const draggable = b.status === "confirmed";
              const timeLabel = new Date(b.start_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
              const durationMin = Math.round((end - start) / 60000);
              const hasConflict = overlapIds.has(b.id);
              const { lane, lanes } = laneMap.get(b.id) || { lane: 0, lanes: 1 };
              // Lay out side-by-side within an 8px gutter, with a 2px gap between lanes when overlapping
              const laneWidth = `calc((100% - 16px) / ${lanes}${lanes > 1 ? " - 2px" : ""})`;
              const laneLeft = `calc(8px + ${lane} * (100% - 16px) / ${lanes}${lane > 0 && lanes > 1 ? " + 2px" : ""})`;
              return (
                <div
                  key={b.id}
                  data-block="1"
                  draggable={draggable}
                  onDragStart={(e) => { e.dataTransfer.setData("booking-id", b.id); e.dataTransfer.effectAllowed = "move"; }}
                  onClick={(e) => { e.stopPropagation(); onSelect(b); }}
                  onMouseDown={onPreview ? (e) => {
                    // Long-press preview: show after 550ms hold without moving away
                    if (e.button !== 0) return;
                    const startX = e.clientX, startY = e.clientY;
                    const target = e.currentTarget;
                    const t = setTimeout(() => onPreview(b, { clientX: startX, clientY: startY }), 550);
                    target._lpTimer = t;
                    const cleanup = () => { clearTimeout(target._lpTimer); document.removeEventListener("mouseup", cleanup); document.removeEventListener("mousemove", moveCheck); };
                    const moveCheck = (ev) => {
                      if (Math.abs(ev.clientX - startX) > 6 || Math.abs(ev.clientY - startY) > 6) cleanup();
                    };
                    document.addEventListener("mouseup", cleanup, { once: false });
                    document.addEventListener("mousemove", moveCheck);
                  } : undefined}
                  onContextMenu={onPreview ? (e) => { e.preventDefault(); onPreview(b, e); } : undefined}
                  className={`absolute rounded-xl ${isCompact ? "px-2 py-0.5 text-[11px]" : "px-3 py-1.5 text-xs"} text-white overflow-hidden hover:z-20 hover:ring-2 hover:ring-white transition cursor-move flex items-center gap-2 ${translucent ? "opacity-70" : ""} ${hasConflict ? "ring-2 ring-red-500" : ""}`}
                  style={{
                    top,
                    height,
                    backgroundColor: bg,
                    left: laneLeft,
                    width: laneWidth,
                    boxShadow: hasConflict ? "0 0 0 2px rgba(239,68,68,0.6), 0 4px 12px rgba(0,0,0,0.08)" : "0 4px 12px rgba(0,0,0,0.08)",
                    zIndex: hasConflict ? 5 + lane : 2 + lane,
                  }}
                  data-testid={`day-block-${b.id}`}
                  data-lane={lane}
                  data-lanes={lanes}
                >
                  {hasConflict && (
                    <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-600 text-white text-[9px] font-black shrink-0 pointer-events-none" title="Conflitto con un altro appuntamento" data-testid={`conflict-${b.id}`}>
                      ⚠
                    </span>
                  )}
                  <div className="font-semibold whitespace-nowrap pointer-events-none tabular-nums">{timeLabel}</div>
                  {lanes === 1 && <div className="text-[10px] opacity-75 whitespace-nowrap pointer-events-none tabular-nums">({durationMin}min)</div>}
                  <div className="truncate opacity-95 pointer-events-none flex-1 min-w-0">
                    <span className="font-medium">{b.service_name}</span>
                    <span className="opacity-80"> · {b.customer_name}</span>
                    {b.staff_name && !isCompact && lanes === 1 && <span className="opacity-70"> · {b.staff_name}</span>}
                  </div>
                </div>
              );
            });
          })()}
          {todaysItems.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-zinc-400 pointer-events-none dark:text-zinc-500" data-testid="day-empty">Nessun appuntamento · click per creare</div>
          )}
        </div>
      </div>
      <DayRevenueFooter items={todaysItems} />
    </div>
  );
}

// Shared revenue footer (used also by Standard/Compact layouts)
function DayRevenueFooter({ items }) {
  const earned = items.filter((b) => b.status === "completed").reduce((s, b) => s + (Number(b.price) || 0), 0);
  const scheduled = items.filter((b) => ["confirmed", "in_progress"].includes(b.status)).reduce((s, b) => s + (Number(b.price) || 0), 0);
  const total = earned + scheduled;
  const counts = {
    confirmed: items.filter((b) => b.status === "confirmed").length,
    in_progress: items.filter((b) => b.status === "in_progress").length,
    completed: items.filter((b) => b.status === "completed").length,
    no_show: items.filter((b) => b.status === "no_show").length,
    cancelled: items.filter((b) => b.status === "cancelled").length,
  };
  return (
    <div className="border-t border-zinc-100 bg-zinc-50 px-5 py-3 flex flex-wrap items-center justify-between gap-4 dark:border-zinc-800 dark:bg-zinc-800" data-testid="day-revenue-footer">
      <div className="flex flex-wrap gap-4 text-xs">
        <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-emerald-600" /><span className="text-zinc-600 dark:text-zinc-400">Completati</span> <span className="font-semibold tabular-nums">{counts.completed}</span></div>
        <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-blue-600" /><span className="text-zinc-600 dark:text-zinc-400">Confermati</span> <span className="font-semibold tabular-nums">{counts.confirmed}</span></div>
        {counts.in_progress > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-violet-600" /><span className="text-zinc-600 dark:text-zinc-400">In corso</span> <span className="font-semibold tabular-nums">{counts.in_progress}</span></div>}
        {counts.no_show > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500" /><span className="text-zinc-600 dark:text-zinc-400">No-show</span> <span className="font-semibold tabular-nums">{counts.no_show}</span></div>}
        {counts.cancelled > 0 && <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-zinc-400" /><span className="text-zinc-600 dark:text-zinc-400">Annullati</span> <span className="font-semibold tabular-nums">{counts.cancelled}</span></div>}
      </div>
      <div className="flex items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400"><span>Incasso</span><span className="font-bold text-emerald-700 tabular-nums dark:text-emerald-300" data-testid="day-revenue-earned">€{earned.toFixed(0)}</span></div>
        <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400"><span>Da incassare</span><span className="font-medium tabular-nums" data-testid="day-revenue-scheduled">€{scheduled.toFixed(0)}</span></div>
        <div className="flex items-center gap-1.5 text-zinc-500 dark:text-zinc-400"><span>Totale</span><span className="font-bold text-zinc-900 tabular-nums dark:text-zinc-100" data-testid="day-revenue-total">€{total.toFixed(0)}</span></div>
      </div>
    </div>
  );
}

// ---------- Variante Staff: una colonna per operatore (ideale palestre/studi) ----------
function DayViewStaff({ items, cursor, hoursMap, staff, onSelect, colorOf, onMove, onCreateAt }) {
  const hourPx = 60;
  const dayStr = ymdLocal(cursor);
  const todaysItems = items.filter((b) => ymdLocal(b.start_at) === dayStr);
  const range = getDayRange(hoursMap, cursor);
  const hasItems = todaysItems.some((b) => b.status !== "cancelled");
  const ext = hasItems ? extendedRange(range, todaysItems) : { startH: range.active ? range.startH : DEFAULT_START_HOUR, endH: range.active ? range.endH : DEFAULT_END_HOUR };
  const startH = ext.startH;
  const endH = ext.endH;
  const timelineHeight = (endH - startH) * hourPx;

  // Selected staff filter (persisted per business in localStorage)
  const storageKey = `prenotoo_staff_filter_${dayStr}`;
  const [filterMode, setFilterMode] = useState(() => localStorage.getItem("prenotoo_staff_filter_mode") || "all"); // "all" | "selected"
  const [selectedStaffIds, setSelectedStaffIds] = useState(() => {
    const raw = localStorage.getItem("prenotoo_staff_filter_ids");
    try { return raw ? new Set(JSON.parse(raw)) : new Set(staff.map((s) => s.id)); } catch { return new Set(staff.map((s) => s.id)); }
  });
  const [showPicker, setShowPicker] = useState(false);

  // Keep selected set in sync when staff list changes
  useEffect(() => {
    const raw = localStorage.getItem("prenotoo_staff_filter_ids");
    if (!raw) setSelectedStaffIds(new Set(staff.map((s) => s.id)));
  }, [staff]);

  function persistIds(set) {
    localStorage.setItem("prenotoo_staff_filter_ids", JSON.stringify(Array.from(set)));
  }
  function toggleStaff(id) {
    setSelectedStaffIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      persistIds(next);
      return next;
    });
  }
  function setMode(m) {
    setFilterMode(m);
    localStorage.setItem("prenotoo_staff_filter_mode", m);
  }

  if (!range.active) {
    return (
      <div className="card-soft p-10 text-center" data-testid="day-closed">
        <div className="font-display text-xl font-medium">Chiuso</div>
      </div>
    );
  }

  const hasUnassigned = todaysItems.some((b) => !b.staff_id);
  const allStaffCols = [
    ...staff.map((s) => ({ id: s.id, name: s.name, color: s.color || "#2563EB" })),
    ...(hasUnassigned ? [{ id: null, name: "Senza staff", color: "#71717A" }] : []),
  ];
  const columns = filterMode === "selected"
    ? allStaffCols.filter((c) => c.id === null || selectedStaffIds.has(c.id))
    : allStaffCols;

  if (allStaffCols.length === 0) {
    return (
      <div className="card-soft p-10 text-center" data-testid="day-view-staff-empty">
        <div className="font-display text-xl font-medium">Nessuno staff configurato</div>
        <div className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">Aggiungi operatori nella tab "Staff" per usare questa vista.</div>
      </div>
    );
  }

  function handleDrop(e, colId) {
    e.preventDefault();
    const id = e.dataTransfer.getData("booking-id");
    if (!id) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top - 8;
    let minutesFromStart = (offsetY / hourPx) * 60;
    minutesFromStart = Math.max(0, Math.round(minutesFromStart / 15) * 15);
    const newStart = new Date(cursor);
    newStart.setHours(startH, 0, 0, 0);
    newStart.setMinutes(newStart.getMinutes() + minutesFromStart);
    onMove(id, newStart, colId);
  }

  function handleClickEmpty(e, colId) {
    if (e.target.dataset.block) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetY = e.clientY - rect.top - 8;
    let minutesFromStart = (offsetY / hourPx) * 60;
    minutesFromStart = Math.max(0, Math.round(minutesFromStart / 15) * 15);
    const newStart = new Date(cursor);
    newStart.setHours(startH, 0, 0, 0);
    newStart.setMinutes(newStart.getMinutes() + minutesFromStart);
    onCreateAt(newStart, colId);
  }

  // Staff with 10+ operators → show picker by default more prominently
  const hasMany = staff.length >= 10;

  return (
    <div data-testid="day-view-staff">
      {/* Filter toolbar */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap" data-testid="staff-filter-bar">
        <div className="flex items-center gap-1 bg-white border border-zinc-200 rounded-full p-1 dark:bg-zinc-900 dark:border-zinc-700">
          <button
            onClick={() => setMode("all")}
            className={`h-7 px-3 text-xs rounded-full font-medium transition ${filterMode === "all" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
            data-testid="staff-filter-all"
          >
            Tutti ({allStaffCols.length})
          </button>
          <button
            onClick={() => setMode("selected")}
            className={`h-7 px-3 text-xs rounded-full font-medium transition ${filterMode === "selected" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
            data-testid="staff-filter-selected"
          >
            Solo selezionati ({selectedStaffIds.size})
          </button>
        </div>
        {filterMode === "selected" && (
          <button
            onClick={() => setShowPicker((v) => !v)}
            className={`h-8 px-3 text-xs rounded-full border font-medium transition ${hasMany ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:bg-blue-950/40 dark:text-blue-300" : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-400"}`}
            data-testid="staff-filter-picker-toggle"
          >
            {showPicker ? "✓ Chiudi selezione" : "Seleziona staff in turno…"}
          </button>
        )}
      </div>

      {showPicker && filterMode === "selected" && (
        <div className="card-soft p-4 mb-3" data-testid="staff-picker">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">Chi è in turno oggi?</div>
            <div className="flex gap-2">
              <button onClick={() => { const n = new Set(staff.map((s) => s.id)); setSelectedStaffIds(n); persistIds(n); }} className="text-xs text-blue-600 hover:underline" data-testid="staff-pick-all">Seleziona tutti</button>
              <button onClick={() => { const n = new Set(); setSelectedStaffIds(n); persistIds(n); }} className="text-xs text-zinc-500 hover:underline dark:text-zinc-400" data-testid="staff-pick-none">Deseleziona</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {staff.map((s) => (
              <button
                key={s.id}
                onClick={() => toggleStaff(s.id)}
                className={`h-8 px-3 text-xs rounded-full font-medium transition flex items-center gap-1.5 border ${selectedStaffIds.has(s.id) ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
                data-testid={`staff-pick-${s.id}`}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color || "#2563EB" }} />
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="card-soft overflow-x-auto">
        <div className="flex min-w-full">
          <div className="w-14 shrink-0 pt-2 border-r border-zinc-100 dark:border-zinc-800" style={{ height: timelineHeight + 72 }}>
            <div className="h-8" />
            <TimelineBg startH={startH} endH={endH} hourPx={hourPx} />
          </div>
          {columns.map((col) => {
            const colItems = todaysItems.filter((b) => (col.id ? b.staff_id === col.id : !b.staff_id));
            // Revenue totals (exclude cancelled/no_show from earnings)
            const earned = colItems.filter((b) => b.status === "completed").reduce((s, b) => s + (Number(b.price) || 0), 0);
            const scheduled = colItems.filter((b) => ["confirmed", "in_progress"].includes(b.status)).reduce((s, b) => s + (Number(b.price) || 0), 0);
            return (
              <div key={col.id || "unassigned"} className="flex-1 min-w-[180px] border-r border-zinc-100 last:border-r-0 dark:border-zinc-800" data-testid={`staff-col-${col.id || "none"}`}>
                <div className="h-8 sticky top-0 bg-white border-b border-zinc-100 px-3 flex items-center gap-2 dark:bg-zinc-900 dark:border-zinc-800">
                  <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: col.color }} />
                  <div className="text-xs font-semibold truncate">{col.name}</div>
                  <div className="text-[10px] text-zinc-400 ml-auto dark:text-zinc-500">{colItems.length}</div>
                </div>
                <div
                  className="relative pt-2 cursor-copy"
                  style={{ height: timelineHeight + 16 }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => handleDrop(e, col.id)}
                  onClick={(e) => handleClickEmpty(e, col.id)}
                >
                  <div className="absolute inset-0 pt-2">
                    {Array.from({ length: endH - startH }).map((_, i) => (
                      <div key={i} style={{ height: hourPx }} className="border-t border-zinc-50 dark:border-zinc-800/60" />
                    ))}
                  </div>
                  {sameDay(cursor, new Date()) && <NowLine startH={startH} endH={endH} hourPx={hourPx} />}
                  {(() => {
                    // Detect conflicts within this staff column
                    const conflictSet = new Set();
                    for (let i = 0; i < colItems.length; i++) {
                      if (colItems[i].status === "cancelled") continue;
                      const s1 = new Date(colItems[i].start_at).getTime();
                      const e1 = new Date(colItems[i].end_at).getTime();
                      for (let j = i + 1; j < colItems.length; j++) {
                        if (colItems[j].status === "cancelled") continue;
                        const s2 = new Date(colItems[j].start_at).getTime();
                        const e2 = new Date(colItems[j].end_at).getTime();
                        if (!(e1 <= s2 || e2 <= s1)) { conflictSet.add(colItems[i].id); conflictSet.add(colItems[j].id); }
                      }
                    }
                    const colLaneMap = computeLanes(colItems);
                    return colItems.map((b) => {
                      const start = new Date(b.start_at);
                      const end = new Date(b.end_at);
                      const top = ((mins(start) - startH * 60) / 60) * hourPx + 8;
                      const height = Math.max(22, ((end - start) / 60000 / 60) * hourPx - 2);
                      const bg = colorOf(b);
                      const draggable = b.status === "confirmed";
                      const timeLabel = new Date(b.start_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
                      const durationMin = Math.round((end - start) / 60000);
                      const hasConflict = conflictSet.has(b.id);
                      const { lane, lanes } = colLaneMap.get(b.id) || { lane: 0, lanes: 1 };
                      const laneWidth = `calc((100% - 12px) / ${lanes}${lanes > 1 ? " - 2px" : ""})`;
                      const laneLeft = `calc(6px + ${lane} * (100% - 12px) / ${lanes}${lane > 0 && lanes > 1 ? " + 2px" : ""})`;
                      return (
                        <div
                          key={b.id}
                          data-block="1"
                          draggable={draggable}
                          onDragStart={(e) => { e.dataTransfer.setData("booking-id", b.id); e.dataTransfer.effectAllowed = "move"; }}
                          onClick={(e) => { e.stopPropagation(); onSelect(b); }}
                          className={`absolute rounded-lg px-2 py-1 text-white text-[11px] overflow-hidden hover:z-20 hover:ring-2 hover:ring-white transition cursor-move flex items-center gap-1.5 ${hasConflict ? "ring-2 ring-red-500" : ""}`}
                          style={{
                            top,
                            height,
                            backgroundColor: bg,
                            left: laneLeft,
                            width: laneWidth,
                            boxShadow: hasConflict ? "0 0 0 2px rgba(239,68,68,0.6), 0 2px 8px rgba(0,0,0,0.08)" : "0 2px 8px rgba(0,0,0,0.08)",
                            zIndex: hasConflict ? 5 + lane : 2 + lane,
                          }}
                          data-testid={`staff-block-${b.id}`}
                          data-lane={lane}
                          data-lanes={lanes}
                        >
                          {hasConflict && (
                            <span className="inline-flex items-center justify-center h-4 w-4 rounded-full bg-red-600 text-white text-[9px] font-black shrink-0 pointer-events-none" data-testid={`staff-conflict-${b.id}`}>⚠</span>
                          )}
                          <div className="font-semibold whitespace-nowrap pointer-events-none tabular-nums">{timeLabel}</div>
                          {lanes === 1 && <div className="text-[9px] opacity-75 whitespace-nowrap pointer-events-none tabular-nums">({durationMin}m)</div>}
                          <div className="truncate opacity-95 pointer-events-none flex-1 min-w-0">
                            <span className="font-medium">{b.service_name}</span>
                            <span className="opacity-80"> · {b.customer_name}</span>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
                {/* Revenue footer */}
                <div className="h-16 bg-zinc-50 border-t border-zinc-100 px-3 py-2 text-[10px] space-y-0.5 dark:bg-zinc-800 dark:border-zinc-800" data-testid={`staff-col-revenue-${col.id || "none"}`}>
                  <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                    <span>Incasso</span>
                    <span className="font-semibold text-emerald-700 tabular-nums dark:text-emerald-300">€{earned.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400 dark:text-zinc-500">
                    <span>Da incassare</span>
                    <span className="tabular-nums">€{scheduled.toFixed(0)}</span>
                  </div>
                  <div className="flex justify-between text-zinc-400 dark:text-zinc-500">
                    <span>Totale giorno</span>
                    <span className="tabular-nums font-medium">€{(earned + scheduled).toFixed(0)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- Variante Griglia Densa: tabella slot 30min × staff (situazioni estreme) ----------
function DayViewGrid({ items, cursor, hoursMap, staff, onSelect, colorOf }) {
  const dayStr = ymdLocal(cursor);
  const todaysItems = items.filter((b) => ymdLocal(b.start_at) === dayStr);
  const range = getDayRange(hoursMap, cursor);
  const hasItems = todaysItems.some((b) => b.status !== "cancelled");
  const ext = hasItems ? extendedRange(range, todaysItems) : { startH: range.active ? range.startH : DEFAULT_START_HOUR, endH: range.active ? range.endH : DEFAULT_END_HOUR };
  const startH = ext.startH;
  const endH = ext.endH;

  if (!range.active && todaysItems.length === 0) {
    return <div className="card-soft p-10 text-center"><div className="font-display text-xl">Chiuso</div></div>;
  }

  // 30-minute slots
  const slots = [];
  for (let h = startH; h < endH; h++) {
    slots.push(h * 60);
    slots.push(h * 60 + 30);
  }

  const hasUnassigned = todaysItems.some((b) => !b.staff_id);
  const columns = [
    ...staff.map((s) => ({ id: s.id, name: s.name, color: s.color || "#2563EB" })),
    ...(hasUnassigned ? [{ id: null, name: "—", color: "#71717A" }] : []),
  ];

  if (columns.length === 0) {
    // Fallback: single column
    columns.push({ id: "__all__", name: "Tutti", color: "#2563EB" });
  }

  function bookingsInCell(colId, slotMin) {
    return todaysItems.filter((b) => {
      if (colId !== "__all__" && (colId ? b.staff_id !== colId : !!b.staff_id)) return false;
      const bs = mins(new Date(b.start_at));
      const be = mins(new Date(b.end_at));
      return bs < slotMin + 30 && be > slotMin;
    });
  }

  return (
    <div className="card-soft overflow-x-auto" data-testid="day-view-grid">
      <table className="w-full min-w-[600px] text-xs border-collapse">
        <thead>
          <tr className="sticky top-0 bg-white border-b border-zinc-200 dark:bg-zinc-900 dark:border-zinc-700">
            <th className="w-16 py-2 text-left text-[10px] uppercase tracking-widest text-zinc-400 font-semibold px-2 dark:text-zinc-500">Ora</th>
            {columns.map((c) => (
              <th key={c.id || "unassigned"} className="py-2 px-2 text-left font-semibold border-l border-zinc-100 dark:border-zinc-800" data-testid={`grid-col-${c.id || "none"}`}>
                <div className="flex items-center gap-1.5">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: c.color }} />
                  <span className="truncate">{c.name}</span>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {slots.map((slotMin) => {
            const isHourStart = slotMin % 60 === 0;
            const hh = Math.floor(slotMin / 60);
            const mm = slotMin % 60;
            return (
              <tr key={slotMin} className={`${isHourStart ? "border-t border-zinc-200" : "border-t border-zinc-50"}`} data-testid={`grid-row-${slotMin}`}>
                <td className={`px-2 py-1 text-[10px] font-semibold text-zinc-500 align-top ${isHourStart ? "" : "text-zinc-300"}`}>
                  {String(hh).padStart(2, "0")}:{String(mm).padStart(2, "0")}
                </td>
                {columns.map((c) => {
                  const bs = bookingsInCell(c.id, slotMin);
                  return (
                    <td key={c.id || "unassigned"} className="px-1 py-0.5 border-l border-zinc-100 align-top dark:border-zinc-800" style={{ height: 28 }}>
                      <div className="flex flex-wrap gap-0.5">
                        {bs.map((b) => {
                          const initials = (b.customer_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                          return (
                            <button
                              key={b.id}
                              onClick={() => onSelect(b)}
                              title={`${b.customer_name} · ${b.service_name} · ${new Date(b.start_at).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`}
                              className="h-5 px-1.5 rounded text-white text-[10px] font-semibold hover:ring-2 hover:ring-zinc-900 transition"
                              style={{ backgroundColor: colorOf(b) }}
                              data-testid={`grid-block-${b.id}`}
                            >
                              {initials}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function NowLine({ startH, endH, hourPx = HOUR_PX }) {
  const now = new Date();
  const m = mins(now);
  if (m < startH * 60 || m > endH * 60) return null;
  const top = ((m - startH * 60) / 60) * hourPx + 8;
  return (
    <div className="absolute left-0 right-0 z-20 pointer-events-none" style={{ top }}>
      <div className="h-[2px] bg-red-500" />
      <div className="absolute -left-1 -top-[5px] h-3 w-3 rounded-full bg-red-500" />
    </div>
  );
}

function WeekView({ items, cursor, hoursMap, onSelect, colorOf, onPickDay, onMove, onCreateAt }) {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(d.getDate() + i); return d; });
  const { startH, endH } = getRangeForDays(hoursMap, days);
  const timelineHeight = (endH - startH) * HOUR_PX;

  function makeStart(day, clientY, rect) {
    const offsetY = clientY - rect.top - 8;
    let minutesFromStart = (offsetY / HOUR_PX) * 60;
    minutesFromStart = Math.max(0, Math.round(minutesFromStart / 15) * 15);
    const d = new Date(day);
    d.setHours(startH, 0, 0, 0);
    d.setMinutes(d.getMinutes() + minutesFromStart);
    return d;
  }

  return (
    <div className="card-soft overflow-hidden">
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)" }}>
        <div className="border-r border-b border-zinc-100 dark:border-zinc-800"></div>
        {days.map((d) => {
          const isToday = sameDay(d, new Date());
          return (
            <button key={d.toISOString()} onClick={() => onPickDay(d)} className="p-2 border-l border-b border-zinc-100 text-left hover:bg-zinc-50 dark:border-zinc-800 dark:hover:bg-zinc-800" data-testid={`week-day-header-${ymdLocal(d)}`}>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 dark:text-zinc-400">{d.toLocaleDateString("it-IT", { weekday: "short" })}</div>
              <div className={`font-display text-xl ${isToday ? "inline-flex h-8 w-8 items-center justify-center rounded-full bg-zinc-900 text-white" : ""}`}>{d.getDate()}</div>
            </button>
          );
        })}
      </div>
      <div className="grid" style={{ gridTemplateColumns: "56px repeat(7, 1fr)", height: timelineHeight + 16 }}>
        <div className="relative pt-2 border-r border-zinc-100 dark:border-zinc-800">
          <TimelineBg startH={startH} endH={endH} />
        </div>
        {days.map((d) => {
          const dayStr = ymdLocal(d);
          const dItems = items.filter((b) => ymdLocal(b.start_at) === dayStr);
          const dRange = getDayRange(hoursMap, d);
          return (
            <div
              key={dayStr}
              className={`relative pt-2 border-l border-zinc-100 ${dRange.active ? "cursor-copy" : "bg-zinc-50/60"}`}
              onDragOver={(e) => { if (dRange.active) e.preventDefault(); }}
              onDrop={(e) => {
                if (!dRange.active) return;
                e.preventDefault();
                const id = e.dataTransfer.getData("booking-id");
                if (!id) return;
                onMove(id, makeStart(d, e.clientY, e.currentTarget.getBoundingClientRect()));
              }}
              onClick={(e) => {
                if (!dRange.active) return;
                if (e.target.dataset.block) return;
                onCreateAt(makeStart(d, e.clientY, e.currentTarget.getBoundingClientRect()));
              }}
              data-testid={`week-col-${dayStr}`}
            >
              <div className="absolute inset-0 pt-2 pointer-events-none">
                {Array.from({ length: endH - startH }).map((_, i) => (
                  <div key={i} style={{ height: HOUR_PX }} className="border-t border-zinc-50 dark:border-zinc-900/50" />
                ))}
              </div>
              {/* Closed-zone overlays outside this day's open window */}
              {dRange.active && dRange.startH > startH && (
                <div
                  className="absolute left-0 right-0 pointer-events-none bg-zinc-100/70 dark:bg-zinc-950/40"
                  style={{ top: 8, height: (dRange.startH - startH) * HOUR_PX }}
                />
              )}
              {dRange.active && dRange.endH < endH && (
                <div
                  className="absolute left-0 right-0 pointer-events-none bg-zinc-100/70 dark:bg-zinc-950/40"
                  style={{ top: (dRange.endH - startH) * HOUR_PX + 8, height: (endH - dRange.endH) * HOUR_PX }}
                />
              )}
              {dRange.active && dRange.hasBreak && (
                <div
                  className="absolute left-0 right-0 pointer-events-none"
                  style={{
                    top: ((dRange.breakStart - startH * 60) / 60) * HOUR_PX + 8,
                    height: ((dRange.breakEnd - dRange.breakStart) / 60) * HOUR_PX,
                    background: "repeating-linear-gradient(45deg, rgba(244,244,245,0.9) 0 6px, rgba(228,228,231,0.9) 6px 12px)",
                  }}
                />
              )}
              {!dRange.active && (
                <div className="absolute inset-0 flex items-start justify-center pt-3 pointer-events-none">
                  <span className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold dark:text-zinc-500">Chiuso</span>
                </div>
              )}
              {sameDay(d, new Date()) && dRange.active && <NowLine startH={startH} endH={endH} />}
              {dItems.map((b) => {
                const sd = new Date(b.start_at);
                const ed = new Date(b.end_at);
                const top = ((mins(sd) - startH * 60) / 60) * HOUR_PX + 8;
                const height = Math.max(18, ((ed - sd) / 60000 / 60) * HOUR_PX - 2);
                const translucent = b.status !== "confirmed";
                const draggable = b.status === "confirmed";
                return (
                  <div
                    key={b.id}
                    data-block="1"
                    draggable={draggable}
                    onDragStart={(e) => { e.dataTransfer.setData("booking-id", b.id); e.dataTransfer.effectAllowed = "move"; }}
                    onClick={(e) => { e.stopPropagation(); onSelect(b); }}
                    className={`absolute left-1 right-1 rounded-lg px-1.5 py-1 text-white text-[10px] leading-tight overflow-hidden hover:z-10 hover:ring-2 hover:ring-white cursor-move ${translucent ? "opacity-70" : ""}`}
                    style={{ top, height, backgroundColor: colorOf(b) }}
                    data-testid={`week-block-${b.id}`}
                  >
                    <div className="font-semibold truncate pointer-events-none">{sd.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}</div>
                    <div className="truncate pointer-events-none">{b.service_name}</div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ items, cursor, onPickDay, colorOf, overlay }) {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startOffset = (first.getDay() + 6) % 7;
  const cells = [];
  const gridStart = new Date(first); gridStart.setDate(1 - startOffset);
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); cells.push(d);
  }
  const weekdays = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
  return (
    <div className="card-soft overflow-hidden">
      <div className="grid grid-cols-7 border-b border-zinc-100 dark:border-zinc-800">
        {weekdays.map((w) => <div key={w} className="p-3 text-[10px] uppercase tracking-widest text-zinc-500 text-center dark:text-zinc-400">{w}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = sameDay(d, new Date());
          const dayStr = ymdLocal(d);
          const dItems = items.filter((b) => ymdLocal(b.start_at) === dayStr && b.status !== "cancelled");
          const holiday = overlay?.holidayFor(d);
          const override = !holiday && overlay?.overrideFor(d);
          const cellTone = holiday ? "bg-rose-50/70" : override ? "bg-amber-50/60" : "";
          const muteText = !inMonth ? "bg-zinc-50/50 text-zinc-400" : "";
          return (
            <button
              key={i}
              onClick={() => onPickDay(new Date(d))}
              className={`relative min-h-[96px] p-2 border-b border-l border-zinc-100 text-left hover:bg-zinc-50 transition ${cellTone || muteText}`}
              data-testid={`month-cell-${dayStr}`}
              title={holiday ? `Chiuso: ${holiday.label}` : override ? `Orario speciale: ${override.label}` : ""}
            >
              <div className="flex items-center justify-between">
                <div className={`font-display text-lg ${isToday ? "inline-flex h-7 w-7 items-center justify-center rounded-full bg-zinc-900 text-white text-sm" : ""}`}>{d.getDate()}</div>
                {holiday && <span className="text-[9px] font-bold uppercase tracking-wider text-rose-700 bg-rose-100 rounded-full px-1.5 py-0.5 dark:text-rose-300 dark:bg-rose-950/40" data-testid={`month-holiday-${dayStr}`}>Chiuso</span>}
                {!holiday && override && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 rounded-full px-1.5 py-0.5 dark:text-amber-300 dark:bg-amber-950/40" data-testid={`month-override-${dayStr}`}>Speciale</span>}
              </div>
              {holiday && <div className="text-[10px] text-rose-700 mt-0.5 truncate dark:text-rose-300">{holiday.label}</div>}
              {!holiday && override && <div className="text-[10px] text-amber-800 mt-0.5 truncate dark:text-amber-300">{override.label}</div>}
              <div className="flex flex-wrap gap-1 mt-1 max-h-12 overflow-hidden">
                {dItems.slice(0, 4).map((b) => (
                  <span key={b.id} className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: colorOf(b) }} />
                ))}
                {dItems.length > 4 && <span className="text-[10px] text-zinc-500 ml-1 dark:text-zinc-400">+{dItems.length - 4}</span>}
              </div>
              {dItems.length > 0 && <div className="text-[10px] text-zinc-500 mt-1 dark:text-zinc-400">{dItems.length} app.</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ActiveFilterChip({ label, dot, onRemove, testid }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900"
      data-testid={testid}
    >
      {dot && <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: dot }} aria-hidden />}
      <span>{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="h-4 w-4 rounded-full inline-flex items-center justify-center bg-white/20 hover:bg-white/40 transition dark:bg-zinc-900/15 dark:hover:bg-zinc-900/30"
        aria-label={`Rimuovi filtro ${label}`}
        data-testid={`${testid}-remove`}
      >
        <X className="h-2.5 w-2.5" />
      </button>
    </span>
  );
}

function FilterRow({ label, hint, count, countTone = "zinc", active, onToggle, testid }) {
  const toneCls = {
    zinc: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    amber: "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300",
    emerald: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300",
  }[countTone];
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      aria-checked={active}
      onClick={onToggle}
      className="w-full flex items-start gap-3 rounded-xl px-3 py-2.5 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition text-left"
      data-testid={testid}
    >
      <span
        className={`mt-0.5 h-4 w-4 shrink-0 rounded border flex items-center justify-center transition ${
          active
            ? "bg-zinc-900 border-zinc-900 dark:bg-zinc-100 dark:border-zinc-100"
            : "border-zinc-300 dark:border-zinc-600"
        }`}
        aria-hidden
      >
        {active && <Check className={`h-3 w-3 ${active ? "text-white dark:text-zinc-900" : ""}`} />}
      </span>
      <span className="flex-1 min-w-0">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{label}</span>
          {typeof count === "number" && count > 0 && (
            <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold tabular-nums ${toneCls}`}>{count}</span>
          )}
        </span>
        {hint && <span className="block text-[11px] text-zinc-500 mt-0.5 dark:text-zinc-400">{hint}</span>}
      </span>
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="card-soft p-4">
      <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}


// ---------- Analytics ----------
function Analytics({ businessId }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/businesses/${businessId}/analytics`).then((r) => setData(r.data));
  }, [businessId]);

  if (!data) return <div className="text-sm text-zinc-500 dark:text-zinc-400">Caricamento analytics…</div>;

  const cards = [
    { label: "Oggi", count: data.today.count, revenue: data.today.revenue },
    { label: "Questa settimana", count: data.week.count, revenue: data.week.revenue },
    { label: "Questo mese", count: data.month.count, revenue: data.month.revenue },
  ];

  return (
    <div>
      <h3 className="font-display text-xl font-medium mb-3">Performance</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="card-soft p-5" data-testid={`analytics-${c.label}`}>
            <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">{c.label}</div>
            <div className="font-display text-4xl mt-2">{c.count}</div>
            <div className="text-sm text-zinc-500 mt-1 dark:text-zinc-400">prenotazioni</div>
            <div className="mt-3 pt-3 border-t border-zinc-100 dark:border-zinc-800">
              <div className="text-xs text-zinc-500 dark:text-zinc-400">Fatturato stimato</div>
              <div className="font-display text-2xl text-emerald-600">€{Number(c.revenue || 0).toFixed(2)}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="card-soft p-5" data-testid="analytics-saturation">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Saturazione agenda (settimana)</div>
          <div className="mt-3 flex items-end gap-3">
            <div className="font-display text-5xl">{data.saturation_week}%</div>
            <div className="text-sm text-zinc-500 pb-2 dark:text-zinc-400">occupata</div>
          </div>
          <div className="mt-3 h-2 bg-zinc-100 rounded-full overflow-hidden dark:bg-zinc-800">
            <div className="h-full bg-blue-600 transition-all" style={{ width: `${Math.min(100, data.saturation_week)}%` }} />
          </div>
        </div>
        <div className="card-soft p-5" data-testid="analytics-noshow">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Tasso no-show</div>
          <div className="mt-3 flex items-end gap-3">
            <div className={`font-display text-5xl ${data.no_show_rate > 10 ? "text-red-600" : "text-zinc-900 dark:text-zinc-100"}`}>{data.no_show_rate}%</div>
          </div>
          <div className="text-xs text-zinc-500 mt-2 dark:text-zinc-400">Clienti che non si sono presentati sul totale degli appuntamenti conclusi.</div>
        </div>
      </div>

      {data.staff?.length > 0 && (
        <div className="card-soft p-5">
          <h4 className="font-display text-lg font-medium mb-4">Performance staff (mese)</h4>
          <div className="space-y-3">
            {data.staff.map((s) => (
              <div key={s.id} className="flex items-center gap-3" data-testid={`analytics-staff-${s.id}`}>
                <div className="flex-1">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-zinc-500 dark:text-zinc-400">{s.bookings} prenotazioni</div>
                </div>
                <div className="font-display text-lg text-emerald-600">€{Number(s.revenue || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.avg_real_duration > 0 && (
        <div className="card-soft p-5 mt-4" data-testid="analytics-real-duration">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div>
              <h4 className="font-display text-lg font-medium">Durata media reale dei servizi</h4>
              <p className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Basata su check-in/completato degli ultimi 90 giorni. Se differisce dalla durata impostata, puoi calibrare meglio i tempi dei servizi.</p>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl">{data.avg_real_duration} min</div>
              <div className="text-[11px] text-zinc-500 uppercase tracking-wide dark:text-zinc-400">Media complessiva</div>
            </div>
          </div>
          {data.service_avg_durations?.length > 0 && (
            <div className="mt-4 space-y-2">
              {data.service_avg_durations.map((s) => (
                <div key={s.service_id} className="flex items-center justify-between gap-3 text-sm" data-testid={`analytics-svc-duration-${s.service_id}`}>
                  <div className="truncate">{s.service_name}</div>
                  <div className="text-zinc-600 dark:text-zinc-400">{s.avg_real_min} min <span className="text-zinc-400 text-xs dark:text-zinc-500">({s.sample_size} campioni)</span></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <IdleGaps businessId={businessId} />
    </div>
  );
}


function IdleGaps({ businessId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/businesses/${businessId}/idle-gaps`, { params: { days: 7 } }).then((r) => setData(r.data)).catch(() => setData({ gaps: [], tetris_score: 0 }));
  }, [businessId]);
  if (!data) return null;

  const idleH = Math.round((data.total_idle_min || 0) / 6) / 10; // 1 decimal
  const unfillable = (data.gaps || []).filter((g) => g.unfillable).length;
  const fillable = (data.gaps || []).filter((g) => !g.unfillable);

  return (
    <div className="mt-6 card-soft p-5" data-testid="analytics-idle-gaps">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div>
          <h4 className="font-display text-lg font-medium">Ottimizzazione agenda (prossimi 7 giorni)</h4>
          <p className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Tempi morti tra un appuntamento e l'altro. Meno ne hai, più alto è lo "score Tetris".</p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Score Tetris</div>
          <div className={`font-display text-3xl ${data.tetris_score >= 70 ? "text-emerald-600" : data.tetris_score >= 40 ? "text-amber-600" : "text-red-600"}`} data-testid="tetris-score">{data.tetris_score}%</div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 text-sm">
        <div className="rounded-xl bg-zinc-50 p-3 dark:bg-zinc-800">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Tempi morti totali</div>
          <div className="font-display text-2xl mt-1">{idleH}h</div>
        </div>
        <div className="rounded-xl bg-zinc-50 p-3 dark:bg-zinc-800">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Buchi riempibili</div>
          <div className="font-display text-2xl mt-1 text-emerald-700 dark:text-emerald-300">{fillable.length}</div>
        </div>
        <div className="rounded-xl bg-zinc-50 p-3 dark:bg-zinc-800">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold dark:text-zinc-400">Buchi troppo corti</div>
          <div className="font-display text-2xl mt-1 text-amber-700 dark:text-amber-300">{unfillable}</div>
          <div className="text-[10px] text-zinc-500 mt-0.5 dark:text-zinc-400">&lt; {data.min_service_min} min</div>
        </div>
      </div>

      {fillable.length === 0 ? (
        <div className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="idle-empty">Nessun buco rilevante da riempire. 🎯</div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Slot ottimali per promozioni / messaggi ai clienti</div>
          {fillable.slice(0, 8).map((g, i) => (
            <div key={i} className="flex items-center gap-3 text-sm py-2 border-b border-zinc-100 last:border-0 dark:border-zinc-800" data-testid={`idle-gap-${i}`}>
              <div className="flex-1">
                <div className="font-medium">
                  {new Date(g.start).toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" })}
                  {" · "}
                  {new Date(g.start).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                  {" – "}
                  {new Date(g.end).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="text-xs text-zinc-500 dark:text-zinc-400">Finestra di {g.duration_min} minuti libera</div>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium dark:bg-emerald-950/40 dark:text-emerald-300">{g.duration_min} min</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Rules ----------
function Rules({ businessId }) {
  const [form, setForm] = useState({ min_cancel_hours: 0, max_active_bookings: 0, max_advance_days: 60, services_layout: "auto", combo_enabled: false, combo_staff_mode: "auto" });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    api.get(`/businesses/${businessId}/settings`).then((r) => setForm({
      min_cancel_hours: r.data.min_cancel_hours || 0,
      max_active_bookings: r.data.max_active_bookings || 0,
      max_advance_days: r.data.max_advance_days ?? 60,
      services_layout: r.data.services_layout || "auto",
      combo_enabled: !!r.data.combo_enabled,
      combo_staff_mode: r.data.combo_staff_mode || "auto",
    }));
  }, [businessId]);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/businesses/${businessId}/settings`, {
        min_cancel_hours: Number(form.min_cancel_hours),
        max_active_bookings: Number(form.max_active_bookings),
        max_advance_days: Math.max(1, Math.min(365, Number(form.max_advance_days) || 60)),
        services_layout: form.services_layout,
        combo_enabled: !!form.combo_enabled,
        combo_staff_mode: form.combo_staff_mode,
      });
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="max-w-xl">
      <h3 className="font-display text-xl font-medium">Regole aziendali</h3>
      <p className="text-sm text-zinc-500 mt-1 mb-6 dark:text-zinc-400">Configura le politiche che i clienti devono rispettare durante la prenotazione.</p>
      <form onSubmit={save} className="card-soft p-6 space-y-5" data-testid="rules-form">
        <Field label="Anticipo minimo per cancellazione (ore)">
          <input type="number" min={0} max={168} className="input-soft" value={form.min_cancel_hours} onChange={(e) => setForm({ ...form, min_cancel_hours: e.target.value })} data-testid="rules-cancel-hours" />
          <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">0 = il cliente può annullare in qualsiasi momento. Es. 24 = deve annullare almeno 24 ore prima.</div>
        </Field>
        <Field label="Limite prenotazioni attive per cliente">
          <input type="number" min={0} className="input-soft" value={form.max_active_bookings} onChange={(e) => setForm({ ...form, max_active_bookings: e.target.value })} data-testid="rules-max-active" />
          <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">0 = illimitato. Es. 3 = ogni cliente può avere al massimo 3 appuntamenti futuri.</div>
        </Field>
        <Field label="Quanti giorni in avanti i clienti possono prenotare?">
          <input type="number" min={1} max={365} className="input-soft" value={form.max_advance_days} onChange={(e) => setForm({ ...form, max_advance_days: e.target.value })} data-testid="rules-max-advance-days" />
          <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Default 60 giorni. Imposta 7 se accetti solo prenotazioni entro la settimana, 365 per pianificare a lungo termine.</div>
        </Field>

        <div className="border-t border-zinc-100 pt-5 dark:border-zinc-800">
          <Field label="Vetrina servizi sulla pagina di prenotazione">
            <select className="input-soft" value={form.services_layout} onChange={(e) => setForm({ ...form, services_layout: e.target.value })} data-testid="rules-services-layout">
              <option value="auto">Auto · scelgo io in base al numero di servizi</option>
              <option value="grid">Griglia card · ideale fino a 8 servizi</option>
              <option value="list">Lista per categoria · ideale 9-30 servizi</option>
              <option value="search">Menu con ricerca · ideale 30+ servizi</option>
            </select>
            <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">In modalità "auto" la vista cambia in base al numero di servizi (≤8 grid, 9-30 lista, 30+ ricerca).</div>
          </Field>
        </div>

        <div className="border-t border-zinc-100 pt-5 space-y-4 dark:border-zinc-800">
          <Field label="Combo · multi-appuntamento sequenziale">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" className="mt-1 h-4 w-4 rounded" checked={!!form.combo_enabled} onChange={(e) => setForm({ ...form, combo_enabled: e.target.checked })} data-testid="rules-combo-enabled" />
              <span className="text-sm">
                <span className="font-medium text-zinc-900 dark:text-zinc-100">Abilita combo</span>
                <span className="block text-xs text-zinc-500 mt-0.5 dark:text-zinc-400">Il cliente può prenotare più servizi consecutivi (es. Taglio + Barba) in una sola conferma.</span>
              </span>
            </label>
          </Field>
          {form.combo_enabled && (
            <Field label="Assegnazione operatore in combo">
              <select className="input-soft" value={form.combo_staff_mode} onChange={(e) => setForm({ ...form, combo_staff_mode: e.target.value })} data-testid="rules-combo-staff-mode">
                <option value="auto">Automatica · sistema sceglie staff disponibile</option>
                <option value="customer_choice">Il cliente sceglie lo staff</option>
              </select>
              <div className="text-xs text-zinc-500 mt-1 dark:text-zinc-400">Tutti i servizi della combo verranno assegnati allo stesso operatore.</div>
            </Field>
          )}
        </div>

        <div className="flex items-center gap-3">
          <button disabled={saving} className="btn-accent" data-testid="rules-save">{saving ? "Salvataggio…" : "Salva regole"}</button>
          {savedAt && <span className="text-xs text-emerald-600">Salvato ✓</span>}
        </div>
      </form>
    </div>
  );
}


// ---------- Widget Embed ----------
function WidgetEmbed({ businessId }) {
  const [label, setLabel] = useState("Prenota ora");
  const [color, setColor] = useState("#18181b");
  const [position, setPosition] = useState("br");
  const [copied, setCopied] = useState(false);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const snippet = `<!-- Prenotoo Widget -->
<script src="${backendUrl}/api/widget/v1.js"
        data-business-id="${businessId}"
        data-label="${label.replace(/"/g, "&quot;")}"
        data-color="${color}"
        data-position="${position}"
        defer></script>`;

  async function copy() {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {}
  }

  return (
    <div className="max-w-3xl" data-testid="widget-embed">
      <h3 className="font-display text-xl font-medium">Widget per il tuo sito</h3>
      <p className="text-sm text-zinc-500 mt-1 mb-6 dark:text-zinc-400">
        Incolla questo snippet su WordPress, Wix, Shopify, Squarespace, o qualsiasi sito HTML. Apparirà un pulsante fluttuante che apre la prenotazione in un'anteprima, senza far uscire i clienti dal tuo sito.
      </p>

      <div className="card-soft p-5 mb-6 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Testo pulsante">
            <input className="input-soft" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="widget-label" />
          </Field>
          <Field label="Colore">
            <div className="flex items-center gap-2">
              <input type="color" className="h-11 w-14 rounded-xl border border-zinc-200 cursor-pointer dark:border-zinc-700" value={color} onChange={(e) => setColor(e.target.value)} data-testid="widget-color" />
              <input className="input-soft flex-1" value={color} onChange={(e) => setColor(e.target.value)} />
            </div>
          </Field>
          <Field label="Posizione">
            <select className="input-soft" value={position} onChange={(e) => setPosition(e.target.value)} data-testid="widget-position">
              <option value="br">Basso a destra</option>
              <option value="bl">Basso a sinistra</option>
              <option value="tr">Alto a destra</option>
              <option value="tl">Alto a sinistra</option>
            </select>
          </Field>
        </div>

        <div>
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 mb-2 dark:text-zinc-400">Anteprima pulsante</div>
          <div className="relative rounded-2xl border border-zinc-200 bg-zinc-50 h-32 overflow-hidden dark:border-zinc-700 dark:bg-zinc-800">
            <button
              type="button"
              disabled
              className="absolute inline-flex items-center gap-2 px-5 py-3 rounded-full font-semibold text-sm shadow-lg"
              style={{
                background: color,
                color: "#fff",
                bottom: position.startsWith("b") ? 16 : undefined,
                top: position.startsWith("t") ? 16 : undefined,
                right: position.endsWith("r") ? 16 : undefined,
                left: position.endsWith("l") ? 16 : undefined,
              }}
              data-testid="widget-preview-btn"
            >
              <CalendarClock className="h-4 w-4" />
              {label || "Prenota ora"}
            </button>
          </div>
        </div>
      </div>

      <div className="card-soft p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Codice da incollare</div>
          <button onClick={copy} className="btn-accent h-9 text-xs" data-testid="widget-copy-btn">
            {copied ? <><Check className="h-3.5 w-3.5 mr-1.5" />Copiato</> : "Copia snippet"}
          </button>
        </div>
        <pre className="text-xs bg-zinc-900 text-zinc-100 rounded-2xl p-4 overflow-x-auto leading-relaxed" data-testid="widget-snippet">
{snippet}
        </pre>
        <div className="text-xs text-zinc-500 mt-3 dark:text-zinc-400">
          Incolla lo snippet prima del tag <code className="px-1 py-0.5 rounded bg-zinc-100 dark:bg-zinc-800">&lt;/body&gt;</code> del tuo sito. Il pulsante apparirà su ogni pagina dove è presente.
        </div>
      </div>
    </div>
  );
}

// ---------- UI Polish: Mini month map ----------
function MiniMonthMap({ cursor, items, onPick, overlay }) {
  const monthCounts = useMemo(() => {
    const counts = new Map();
    for (const b of items) {
      if (b.status === "cancelled") continue;
      const k = ymdLocal(b.start_at);
      counts.set(k, (counts.get(k) || 0) + 1);
    }
    return counts;
  }, [items]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstOfMonth = new Date(year, month, 1);
  const startGrid = startOfWeek(firstOfMonth);
  const days = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startGrid);
    d.setDate(d.getDate() + i);
    return d;
  });
  const monthLabel = firstOfMonth.toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const todayKey = ymdLocal(new Date());

  return (
    <div
      className="hidden xl:block fixed right-6 top-44 z-40 w-64 card-soft p-4 backdrop-blur bg-white/95"
      data-testid="mini-month-map"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-zinc-500 dark:text-zinc-400">Mappa</div>
        <div className="text-sm font-display capitalize">{monthLabel}</div>
      </div>
      <div className="grid grid-cols-7 text-[10px] uppercase text-zinc-400 font-semibold mb-1 dark:text-zinc-500">
        {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (
          <span key={i} className="text-center">{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-0.5">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === month;
          const k = ymdLocal(d);
          const count = monthCounts.get(k) || 0;
          const isCursor = sameDay(d, cursor);
          const isToday = k === todayKey;
          const holiday = overlay?.holidayFor(d);
          const override = !holiday && overlay?.overrideFor(d);
          const dayClasses = holiday
            ? "bg-rose-100 text-rose-800 hover:bg-rose-200"
            : override
              ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
              : isCursor
                ? "bg-zinc-900 text-white"
                : inMonth
                  ? "text-zinc-700 hover:bg-zinc-100"
                  : "text-zinc-300 hover:bg-zinc-50";
          const tipParts = [d.toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "short" })];
          if (holiday) tipParts.push(`Chiuso: ${holiday.label}`);
          if (override) tipParts.push(`Orario speciale: ${override.label}`);
          if (count) tipParts.push(`${count} prenotazioni`);
          return (
            <button
              key={i}
              onClick={() => onPick(d)}
              className={`relative h-8 rounded-md text-[11px] font-medium tabular-nums transition ${dayClasses} ${isToday && !isCursor && !holiday && !override ? "ring-1 ring-blue-500" : ""}`}
              data-testid={`mini-day-${k}`}
              title={tipParts.join(" · ")}
            >
              {d.getDate()}
              {count > 0 && (
                <span
                  className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1 rounded-full ${isCursor ? "bg-white/80" : holiday ? "bg-rose-500" : override ? "bg-amber-500" : "bg-blue-500"}`}
                  style={{ width: count > 4 ? 14 : count * 3 + 2 }}
                />
              )}
            </button>
          );
        })}
      </div>
      <div className="mt-3 pt-2 border-t border-zinc-100 grid grid-cols-2 gap-1.5 text-[10px] text-zinc-500 dark:border-zinc-800 dark:text-zinc-400">
        <span className="inline-flex items-center gap-1"><span className="h-1 w-3 rounded-full bg-blue-500" /> appuntamenti</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded ring-1 ring-blue-500" /> oggi</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-rose-200 dark:bg-rose-900/40" /> chiuso</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded bg-amber-200 dark:bg-amber-900/40" /> speciale</span>
      </div>
    </div>
  );
}

// ---------- UI Polish: Long-press preview popover ----------
function BlockPreview({ booking, x, y, onClose }) {
  useEffect(() => {
    function close() { onClose(); }
    document.addEventListener("click", close);
    document.addEventListener("scroll", close, true);
    // Native HTML5 drag-and-drop doesn't fire a click event on the dragged
    // element, so close the preview as soon as the user starts dragging.
    document.addEventListener("dragstart", close, true);
    return () => {
      document.removeEventListener("click", close);
      document.removeEventListener("scroll", close, true);
      document.removeEventListener("dragstart", close, true);
    };
  }, [onClose]);

  const W = 280, H = 180;
  const px = Math.min(window.innerWidth - W - 8, Math.max(8, x + 8));
  const py = Math.min(window.innerHeight - H - 8, Math.max(8, y + 8));

  const start = new Date(booking.start_at);
  const end = new Date(booking.end_at);
  const STATUS_LABELS = {
    confirmed: "Confermato", in_progress: "In corso", completed: "Completato",
    cancelled: "Annullato", no_show: "No-show",
  };
  return (
    <div
      className="fixed z-[70] w-[280px] card-soft bg-white p-4 pointer-events-none dark:bg-zinc-900"
      style={{ left: px, top: py }}
      data-testid="block-preview"
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold dark:text-zinc-400">Anteprima</div>
      <div className="font-display text-base font-medium mt-0.5 truncate">{booking.service_name}</div>
      <div className="text-xs text-zinc-700 mt-1 dark:text-zinc-300">
        <strong>{booking.customer_name}</strong>
      </div>
      <div className="text-xs text-zinc-600 mt-1 dark:text-zinc-400">
        {start.toLocaleDateString("it-IT", { weekday: "long", day: "2-digit", month: "short" })}
        {" · "}
        {start.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}–{end.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
      </div>
      <div className="text-xs text-zinc-600 mt-0.5 dark:text-zinc-400">€{booking.price} · {STATUS_LABELS[booking.status] || booking.status}</div>
      {booking.staff_name && <div className="text-xs text-zinc-600 mt-0.5 dark:text-zinc-400">Operatore: {booking.staff_name}</div>}
      {booking.notes && <div className="text-[11px] text-zinc-500 mt-2 line-clamp-3 italic dark:text-zinc-400">"{booking.notes}"</div>}
      <div className="text-[10px] text-zinc-400 mt-2 dark:text-zinc-500">Click per aprire i dettagli</div>
    </div>
  );
}

// ---------- UI Polish: Keyboard shortcuts help ----------
function ShortcutsHelp({ onClose }) {
  const items = [
    { k: "j  ·  →", desc: "Periodo successivo" },
    { k: "k  ·  ←", desc: "Periodo precedente" },
    { k: "t", desc: "Vai a Oggi" },
    { k: "1 · 2 · 3 · 4", desc: "Vista Lista / Giorno / Settimana / Mese" },
    { k: "n", desc: "Nuovo appuntamento" },
    { k: "Tieni premuto", desc: "Anteprima rapida sull'appuntamento (no apertura modal)" },
    { k: "Esc", desc: "Chiudi modal o anteprima" },
    { k: "?", desc: "Mostra questa schermata" },
  ];
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="shortcuts-help">
      <div className="bg-white rounded-3xl card-soft w-full max-w-md p-6 dark:bg-zinc-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Velocità</div>
            <h3 className="font-display text-2xl font-medium tracking-tight">Scorciatoie</h3>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center dark:bg-zinc-800 dark:hover:bg-zinc-700" data-testid="shortcuts-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <ul className="space-y-2.5">
          {items.map((it) => (
            <li key={it.k} className="flex items-center gap-3">
              <kbd className="text-xs font-mono px-2 py-1 rounded-md bg-zinc-100 border border-zinc-200 text-zinc-700 shrink-0 min-w-[110px] text-center dark:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300">
                {it.k}
              </kbd>
              <span className="text-sm text-zinc-700 dark:text-zinc-300">{it.desc}</span>
            </li>
          ))}
        </ul>
        <div className="text-[11px] text-zinc-400 mt-4 pt-3 border-t border-zinc-100 dark:text-zinc-500 dark:border-zinc-800">
          Le scorciatoie si disattivano mentre digiti in un campo di testo.
        </div>
      </div>
    </div>
  );
}



function MoveConfirmModal({ info, onCancel, onConfirm }) {
  const fmt = (d) => d.toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" });
  const staffChanged = info.fromStaffName !== info.toStaffName;
  const timeChanged = info.fromDate.getTime() !== info.toDate.getTime();
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="move-confirm-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Conferma spostamento</div>
        <h2 className="font-display text-2xl font-medium mt-1 mb-4">Spostare l'appuntamento?</h2>

        <div className="rounded-2xl bg-zinc-50 p-4 mb-4 space-y-1 dark:bg-zinc-800">
          <div className="font-semibold">{info.serviceName}</div>
          <div className="text-sm text-zinc-600 dark:text-zinc-400">{info.customerName}</div>
        </div>

        {timeChanged && (
          <div className="mb-3" data-testid="move-confirm-time">
            <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-1 dark:text-zinc-500">Orario</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800">
                <div className="text-[10px] text-zinc-400 uppercase dark:text-zinc-500">Da</div>
                <div className="text-sm font-medium text-zinc-600 capitalize dark:text-zinc-400" data-testid="move-from-time">{fmt(info.fromDate)}</div>
              </div>
              <div className="text-zinc-400 text-lg dark:text-zinc-500">→</div>
              <div className="flex-1 rounded-xl border-2 border-blue-500 bg-blue-50 px-3 py-2 dark:bg-blue-950/40">
                <div className="text-[10px] text-blue-600 uppercase">A</div>
                <div className="text-sm font-semibold text-blue-900 capitalize dark:text-blue-200" data-testid="move-to-time">{fmt(info.toDate)}</div>
              </div>
            </div>
          </div>
        )}

        {staffChanged && (
          <div className="mb-3" data-testid="move-confirm-staff">
            <div className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-400 mb-1 dark:text-zinc-500">Staff</div>
            <div className="flex items-center gap-3">
              <div className="flex-1 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 dark:border-zinc-700 dark:bg-zinc-800">
                <div className="text-[10px] text-zinc-400 uppercase dark:text-zinc-500">Da</div>
                <div className="text-sm font-medium text-zinc-600 dark:text-zinc-400">{info.fromStaffName || "Non assegnato"}</div>
              </div>
              <div className="text-zinc-400 text-lg dark:text-zinc-500">→</div>
              <div className="flex-1 rounded-xl border-2 border-blue-500 bg-blue-50 px-3 py-2 dark:bg-blue-950/40">
                <div className="text-[10px] text-blue-600 uppercase">A</div>
                <div className="text-sm font-semibold text-blue-900 dark:text-blue-200">{info.toStaffName || "Non assegnato"}</div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-2 mt-5">
          <button onClick={onCancel} className="btn-secondary flex-1" data-testid="move-confirm-cancel">Annulla</button>
          <button onClick={onConfirm} className="btn-accent flex-1" data-testid="move-confirm-ok">Conferma spostamento</button>
        </div>
      </div>
    </div>
  );
}


function ManualMoveModal({ booking, onCancel, onConfirm }) {
  const startDt = new Date(booking.start_at);
  const pad = (n) => String(n).padStart(2, "0");
  const initial = `${startDt.getFullYear()}-${pad(startDt.getMonth() + 1)}-${pad(startDt.getDate())}T${pad(startDt.getHours())}:${pad(startDt.getMinutes())}`;
  const [value, setValue] = useState(initial);
  const [err, setErr] = useState("");

  function submit() {
    setErr("");
    if (!value) { setErr("Scegli data e orario"); return; }
    const newDate = new Date(value);
    if (isNaN(newDate.getTime())) { setErr("Data non valida"); return; }
    onConfirm(newDate);
  }

  const sameTime = Math.abs(new Date(value).getTime() - startDt.getTime()) < 60_000;

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="manual-move-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-600">Sposta prenotazione</div>
        <h2 className="font-display text-2xl font-medium mt-1 mb-4">Nuova data e orario</h2>

        <div className="rounded-2xl bg-zinc-50 p-4 mb-4 space-y-0.5 dark:bg-zinc-800">
          <div className="font-semibold">{booking.service_name}</div>
          <div className="text-sm text-zinc-600 dark:text-zinc-400">{booking.customer_name}</div>
          <div className="text-xs text-zinc-500 capitalize mt-1 dark:text-zinc-400">
            Attuale: {startDt.toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>

        <label className="block mb-4">
          <span className="text-[10px] font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nuova data/ora</span>
          <input
            type="datetime-local"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="mt-1 w-full h-12 rounded-xl border border-zinc-300 px-3 text-sm focus:border-blue-500 focus:outline-none dark:border-zinc-600"
            step="900"
            data-testid="manual-move-input"
          />
        </label>

        {err && <div className="text-sm text-rose-600 mb-3" data-testid="manual-move-error">{err}</div>}

        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary flex-1" data-testid="manual-move-cancel">Annulla</button>
          <button
            onClick={submit}
            disabled={sameTime}
            className="btn-accent flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="manual-move-confirm"
          >
            Sposta
          </button>
        </div>
      </div>
    </div>
  );
}


function HardDeleteModal({ booking, onCancel, onConfirm }) {
  const startDt = new Date(booking.start_at);
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="hard-delete-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-md p-6 card-soft dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-600">Elimina definitivamente</div>
        <h2 className="font-display text-2xl font-medium mt-1 mb-2">Sei sicuro?</h2>
        <p className="text-sm text-zinc-600 mb-4 dark:text-zinc-400">
          Questa azione <strong>cancella completamente</strong> la prenotazione e non può essere annullata.
          Il cliente riceverà una notifica se ha installato l'app.
        </p>

        <div className="rounded-2xl bg-rose-50 border border-rose-100 p-4 mb-5 space-y-0.5 dark:bg-rose-950/40 dark:border-rose-900/50">
          <div className="font-semibold text-rose-900 dark:text-rose-200">{booking.service_name}</div>
          <div className="text-sm text-rose-800 dark:text-rose-300">{booking.customer_name}</div>
          <div className="text-xs text-rose-700 capitalize mt-1 dark:text-rose-300">
            {startDt.toLocaleString("it-IT", { weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>

        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-secondary flex-1" data-testid="hard-delete-cancel">Annulla</button>
          <button
            onClick={onConfirm}
            className="flex-1 h-11 rounded-full bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
            data-testid="hard-delete-confirm"
          >
            Elimina definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}


function ManualBookingModal({ businessId, startAt, staff, onClose, onCreated }) {
  const [services, setServices] = useState([]);
  const [picked, setPicked] = useState(null); // CustomerPicker selection or null
  const [form, setForm] = useState({ service_id: "", staff_id: "", notes: "" });
  const [start, setStart] = useState(() => {
    const pad = (n) => String(n).padStart(2, "0");
    return `${startAt.getFullYear()}-${pad(startAt.getMonth() + 1)}-${pad(startAt.getDate())}T${pad(startAt.getHours())}:${pad(startAt.getMinutes())}`;
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/businesses/${businessId}`).then((a) => {
      setServices(a.data.services || []);
    });
  }, [businessId]);

  async function submit(e, force = false) {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.service_id) { setErr("Scegli un servizio"); return; }
    if (!picked) { setErr("Seleziona o crea un cliente"); return; }
    setErr("");
    setSaving(true);
    try {
      const payload = {
        service_id: form.service_id,
        start_at: toLocalIso(new Date(start)),
        staff_id: form.staff_id || null,
        notes: form.notes,
        force,
      };
      if (picked.kind === "user") {
        payload.customer_id = picked.id;
      } else if (picked.kind === "contact") {
        payload.contact_id = picked.contactId;
      } else {
        payload.customer_name = picked.name;
        if (picked.email) payload.customer_email = picked.email;
        if (picked.phone) payload.customer_phone = picked.phone;
      }
      await api.post(`/businesses/${businessId}/bookings/manual`, payload);
      onCreated();
    } catch (ex) {
      const detail = formatErr(ex.response?.data?.detail) || "Errore";
      // 409 = overlap → offer overbooking confirmation
      if (ex.response?.status === 409 && !force) {
        if (window.confirm(`⚠️ ATTENZIONE: ${detail}\n\nStai creando un appuntamento in OVERBOOKING (si sovrappone ad altri già presenti). Solo tu come proprietario puoi forzare.\n\nVuoi procedere comunque?`)) {
          setSaving(false);
          return submit(null, true);
        }
      }
      setErr(detail);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end md:items-center justify-center p-4" onClick={onClose} data-testid="manual-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl w-full max-w-lg card-soft p-6 max-h-[92vh] overflow-y-auto dark:bg-zinc-900">
        <div className="text-xs font-semibold tracking-[0.15em] uppercase text-zinc-500 dark:text-zinc-400">Nuovo appuntamento</div>
        <div className="font-display text-2xl font-medium mt-1">Crea manuale</div>

        <form onSubmit={submit} className="mt-5 space-y-4">
          <Field label="Servizio">
            <select required className="input-soft" value={form.service_id} onChange={(e) => setForm({ ...form, service_id: e.target.value })} data-testid="manual-service">
              <option value="">—</option>
              {services.map((s) => (
                <option key={s.id} value={s.id}>{s.name} · {s.duration_min}min · €{s.price}</option>
              ))}
            </select>
          </Field>

          <Field label="Data e ora">
            <input type="datetime-local" required className="input-soft" value={start} onChange={(e) => setStart(e.target.value)} data-testid="manual-start" />
          </Field>

          <Field label="Cliente">
            <CustomerPicker
              businessId={businessId}
              value={picked}
              onChange={setPicked}
              allowWalkin={true}
              allowCreate={true}
            />
          </Field>

          {staff.length > 0 && (
            <Field label="Operatore (opzionale — auto-assegnato se vuoto)">
              <select className="input-soft" value={form.staff_id} onChange={(e) => setForm({ ...form, staff_id: e.target.value })} data-testid="manual-staff">
                <option value="">Auto</option>
                {staff.filter(s => s.active).map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Note (opzionale)">
            <textarea rows={2} className="input-soft h-auto py-3" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="manual-notes" />
            <p className="text-[10px] text-zinc-400 mt-1 dark:text-zinc-500">Non includere dati sanitari sensibili (DPA art. 9).</p>
          </Field>

          {err && <div className="text-sm text-red-600" data-testid="manual-error">{err}</div>}

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary h-11 text-sm">Annulla</button>
            <button type="submit" disabled={saving} className="flex-1 btn-accent h-11 text-sm" data-testid="manual-submit">
              {saving ? "Creazione…" : "Crea appuntamento"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

